import * as d3 from 'd3';
import {
	DraggableBeforeUpdateCallback,
	DraggableOnUpdateCallback,
	ICanvasOptions,
	ICoordinates,
	ICanvasObjectCreateOptions,
	IUpdateDraggableConfig,
	IDraggableValue,
	Overlaps,
	IArea,
	IOverlapDefinition,
	MIN_ARROW_AREA_PX_WIDTH,
	OverlapReport,
	IConnectionValue,
	IBaseCanvasObjectCreateOptions,
	Placement,
	NonDraggableBeforeUpdateCallback,
	NonDraggableOnUpdateCallback,
	IAbsoluteCanvasObjectCreateOptions,
	IConnectionOptions,
	IDraggableUndoableValue,
	DraggableBeforeDeleteConnectionCallback
} from './defs';
import {
	UndoableEntity,
	addUndoEventListener,
	removeUndoEventListener
} from './canvas-undo-redo';
import { Logger, YieldOptions, YieldTracker } from '@elyx-code/common-ts-utils';

const navigator = window.navigator.userAgent;

let browserVersion = '';
let browserName = '';

switch (true) {
	case navigator.indexOf('Chrome') !== -1:
		browserName = 'Chrome';
		browserVersion = navigator.match(/Chrome\/(\d+)/)[1];
		break;
	case navigator.indexOf('Firefox') !== -1:
		browserName = 'Firefox';
		browserVersion = navigator.match(/Firefox\/(\d+)/)[1];
		break;
	case navigator.indexOf('Safari') !== -1:
		browserName = 'Safari';
		browserVersion = navigator.match(/Safari\/(\d+)/)[1];
		break;
	case navigator.indexOf('MSIE') !== -1:
		browserName = 'IE';
		browserVersion = navigator.match(/MSIE (\d+)/)[1];
		break;
	case navigator.indexOf('Trident') !== -1:
		browserName = 'IE';
		browserVersion = navigator.match(/Trident\/(\d+)/)[1];
		break;
	case navigator.indexOf('Edge') !== -1:
		browserName = 'Edge';
		browserVersion = navigator.match(/Edge\/(\d+)/)[1];
		break;
	default:
		browserName = 'Unknown';
		browserVersion = 'Unknown';
}

Logger.log('Browser name: ', browserName);
Logger.log('Browser version: ', browserVersion);

// Given an HTML element and the canvas element,
// we recursively find the parent until we arrive at the canvas element
// We return the last parent element before the canvas element
export function getCanvasParentElement(
	element: HTMLElement,
	canvasElement: HTMLElement
): HTMLElement | null {
	if (!canvasElement.contains(element)) {
		Logger.warn('Element is not a child of the canvas');
		return null;
	}

	let parent = element.parentElement;

	if (parent === canvasElement) {
		return element;
	}

	return getCanvasParentElement(parent, canvasElement);
}

export function checkHasOverlappedLine(
	startingCursor: { x: number; y: number },
	currentCursor: { x: number; y: number },
	startLine: { x: number; y: number },
	endLine: { x: number; y: number }
): boolean {
	// Direction vectors for the cursor movement and the line segment
	const cursorDirection = {
		x: currentCursor.x - startingCursor.x,
		y: currentCursor.y - startingCursor.y
	};
	const lineDirection = {
		x: endLine.x - startLine.x,
		y: endLine.y - startLine.y
	};

	// Calculate the determinant of the system (based on direction vectors)
	const determinant =
		-cursorDirection.x * lineDirection.y +
		cursorDirection.y * lineDirection.x;

	// If the determinant is 0, lines are parallel and cannot intersect
	if (determinant === 0) return false;

	// Calculate differences between starting points
	const deltaStart = {
		x: startLine.x - startingCursor.x,
		y: startLine.y - startingCursor.y
	};

	// Calculate the parameters t and u for the intersection point
	const t =
		(-lineDirection.y * deltaStart.x + lineDirection.x * deltaStart.y) /
		determinant;
	const u =
		(cursorDirection.x * deltaStart.y - cursorDirection.y * deltaStart.x) /
		determinant;

	// Check if the intersection point is within both line segments
	return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

function getScaledRelativeOffset(
	child: HTMLElement,
	parent: HTMLElement,
	scale: number,
	{
		debug = false
	}: {
		debug?: boolean;
	} = {
		debug: false
	}
) {
	// Get the bounding rectangle of both elements
	const childRect = child.getBoundingClientRect();
	const parentRect = parent.getBoundingClientRect();

	// Calculate relative positions
	const left = childRect.left / scale - parentRect.left / scale;
	const top = childRect.top / scale - parentRect.top / scale;
	const right = childRect.right / scale - parentRect.right / scale;
	const bottom = childRect.bottom / scale - parentRect.bottom / scale;

	return { left, top, right, bottom };
}

function getElementCanvasCoordinates(
	element: HTMLElement,
	parentCanvasElement: HTMLElement,
	scale: number,
	{
		debug = false
	}: {
		debug?: boolean;
	} = {
		debug: false
	}
): {
	top: number;
	left: number;
	right: number;
	bottom: number;
} {
	const parentTop = parseFloat(parentCanvasElement.style.top);
	const parentLeft = parseFloat(parentCanvasElement.style.left);
	const parentWidth =
		parentCanvasElement.offsetWidth | parentCanvasElement.clientWidth;
	const parentHeight =
		parentCanvasElement.offsetHeight | parentCanvasElement.clientHeight;
	const parentRight = parentLeft + parentWidth;
	const parentBottom = parentTop + parentHeight;

	const {
		left: offsetLeft,
		top: offsetTop,
		right: offsetRight,
		bottom: offsetBottom
	} = getScaledRelativeOffset(element, parentCanvasElement, scale, { debug });

	const absoluteTop = parentTop + offsetTop;
	const absoluteLeft = parentLeft + offsetLeft;
	const absoluteRight = parentRight + offsetRight;
	const absoluteBottom = parentBottom + offsetBottom;

	return {
		top: absoluteTop,
		left: absoluteLeft,
		right: absoluteRight,
		bottom: absoluteBottom
	};
}

// function chrome128PlusPositionRelativeTo(
function positionRelativeTo(
	element: HTMLElement,
	anchor: HTMLElement,
	parent: HTMLElement,
	scale: number,
	placement: Placement = 'right',
	arrow?: HTMLElement,
	offset: number = 0,
	{
		debug = false
	}: {
		debug?: boolean;
	} = {
		debug: false
	}
): ICoordinates {
	const parentTop = parseFloat(parent.style.top);
	const parentLeft = parseFloat(parent.style.left);
	const parentWidth = parent.offsetWidth;
	const parentRight = parentLeft + parentWidth;

	const childParentOffset = getScaledRelativeOffset(anchor, parent, scale, {
		debug
	});

	// Calculate the middle of the height of the anchor element
	const anchorVerticalCenterOffsetFromParentTop =
		childParentOffset.top + anchor.offsetHeight / 2;
	const anchorHorizontalCenterOffsetFromParentLeft =
		childParentOffset.left + anchor.offsetWidth / 2;

	// Add the right position of the parent plus the right offset from it of the children
	const elementLeft = parentLeft + childParentOffset.left;
	const elementRight = parentRight + childParentOffset.right;
	const elementTop = parentTop + childParentOffset.top;
	const elementBottom = parentTop + childParentOffset.bottom;
	const elementHeight = element.offsetHeight || element.clientHeight;
	const elementWidth = element.offsetWidth || element.clientWidth;

	let elementNewY = 0;
	let elementNewX = 0;

	if (debug) {
		debugger;
	}

	switch (placement) {
		case 'top':
			if (debug) {
				debugger;
			}
			elementNewY = elementTop - elementHeight + offset;
			elementNewX +=
				anchorHorizontalCenterOffsetFromParentLeft +
				parentLeft -
				elementWidth / 2;
			if (debug) {
				debugger;
			}
			break;
		case 'bottom':
			if (debug) {
				debugger;
			}
			elementNewY = elementTop + anchor.offsetHeight + offset;
			elementNewX +=
				anchorHorizontalCenterOffsetFromParentLeft +
				parentLeft -
				elementWidth / 2;
			if (debug) {
				debugger;
			}
			break;
		case 'left':
			if (debug) {
				debugger;
			}
			elementNewY +=
				anchorVerticalCenterOffsetFromParentTop +
				parentTop -
				elementHeight / 2;
			elementNewX = elementLeft - elementWidth - offset;
			if (debug) {
				debugger;
			}
			break;
		case 'right':
			if (debug) {
				debugger;
			}
			elementNewY +=
				anchorVerticalCenterOffsetFromParentTop +
				parentTop -
				elementHeight / 2;
			elementNewX += elementRight + offset;
			if (debug) {
				debugger;
			}
			break;
		case 'right-start':
			if (debug) {
				debugger;
			}
			elementNewY += elementTop;
			elementNewX += elementRight + offset;
			if (debug) {
				debugger;
			}
			break;
		case 'left-start':
			if (debug) {
				debugger;
			}
			elementNewY += elementTop;
			elementNewX = elementLeft - elementWidth - offset;
			if (debug) {
				debugger;
			}
			break;
		case 'right-end':
			if (debug) {
				debugger;
			}
			elementNewY += elementTop + anchor.offsetHeight - elementHeight;
			elementNewX += elementRight + offset;
			if (debug) {
				debugger;
			}
			break;
		case 'left-end':
			if (debug) {
				debugger;
			}
			elementNewY += elementTop + anchor.offsetHeight - elementHeight;
			elementNewX = elementLeft - elementWidth - offset;
			if (debug) {
				debugger;
			}
			break;
		case 'top-start':
			if (debug) {
				debugger;
			}
			elementNewY = elementTop - elementHeight + offset;
			elementNewX = elementLeft;
			if (debug) {
				debugger;
			}
			break;
		case 'top-end':
			if (debug) {
				debugger;
			}
			elementNewY = elementTop - elementHeight + offset;
			elementNewX = elementRight - elementWidth;
			if (debug) {
				debugger;
			}
			break;
		case 'bottom-start':
			if (debug) {
				debugger;
			}
			elementNewY = elementTop + anchor.offsetHeight + offset;
			elementNewX = elementLeft;
			if (debug) {
				debugger;
			}
			break;
		case 'bottom-end':
			if (debug) {
				debugger;
			}
			elementNewY = elementTop + anchor.offsetHeight + offset;
			elementNewX = elementRight - elementWidth;
			if (debug) {
				debugger;
			}
			break;
		default:
			throw new Error(
				'Invalid canvas object relative position placement'
			);
	}

	if (!!arrow) {
		let arrowWidth = arrow.offsetWidth;
		let arrowHeight = arrow.offsetHeight;

		// Offset the x and y of the main element by half of the size of the arrow
		// And move arrow to the middle of the element
		switch (placement) {
			case 'top':
				elementNewY -= arrowHeight;
				// Move arrow to the horizontal middle of it parent
				arrow.style.left = elementWidth / 2 - arrowWidth / 2 + 'px';
				arrow.style.bottom = -(arrowHeight / 2) + 'px';
				break;
			case 'bottom':
				elementNewY += arrowHeight;
				// Move arrow to the horizontal middle of it parent
				arrow.style.left = elementWidth / 2 - arrowWidth / 2 + 'px';
				arrow.style.top = -(arrowHeight / 2) + 'px';
				break;
			case 'left':
				elementNewX -= arrowWidth;
				// Move arrow to the vertical middle of it parent
				arrow.style.top = elementHeight / 2 - arrowHeight / 2 + 'px';
				arrow.style.right = -(arrowWidth / 2) + 'px';
				break;
			case 'right':
				elementNewX += arrowWidth;
				// Move arrow to the vertical middle of it parent
				arrow.style.top = elementHeight / 2 - arrowHeight / 2 + 'px';
				arrow.style.left = -(arrowWidth / 2) + 'px';
				break;
			case 'right-start':
				elementNewX += arrowWidth;
				// Move arrow to the vertical of the anchor
				// Half of the anchor height
				arrow.style.top =
					anchor.offsetHeight / 2 - arrowHeight / 2 + 'px';
				arrow.style.left = -(arrowWidth / 2) + 'px';
				break;
			case 'left-start':
				elementNewX -= arrowWidth;
				// Move arrow to the vertical of the anchor
				// Half of the anchor height
				arrow.style.top =
					anchor.offsetHeight / 2 - arrowHeight / 2 + 'px';
				arrow.style.right = -(arrowWidth / 2) + 'px';
				break;
			case 'right-end':
				elementNewX += arrowWidth;
				// Move arrow to the vertical of the anchor
				// Half of the anchor height
				arrow.style.bottom =
					anchor.offsetHeight / 2 - arrowHeight / 2 + 'px';
				arrow.style.left = -(arrowWidth / 2) + 'px';
				break;
			case 'left-end':
				elementNewX -= arrowWidth;
				// Move arrow to the vertical of the anchor
				// Half of the anchor height
				arrow.style.bottom =
					anchor.offsetHeight / 2 - arrowHeight / 2 + 'px';
				arrow.style.right = -(arrowWidth / 2) + 'px';
				break;
			case 'top-start':
				elementNewY -= arrowHeight;
				// Move arrow to the horizontal middle of the anchor
				arrow.style.left =
					anchor.offsetWidth / 2 - arrowWidth / 2 + 'px';
				arrow.style.bottom = -(arrowHeight / 2) + 'px';
				break;
			case 'top-end':
				elementNewY -= arrowHeight;
				// Move arrow to the horizontal middle of the anchor
				arrow.style.right =
					anchor.offsetWidth / 2 - arrowWidth / 2 + 'px';
				arrow.style.bottom = -(arrowHeight / 2) + 'px';
				break;
			case 'bottom-start':
				elementNewY += arrowHeight;
				// Move arrow to the horizontal middle of the anchor
				arrow.style.left =
					anchor.offsetWidth / 2 - arrowWidth / 2 + 'px';
				arrow.style.top = -(arrowHeight / 2) + 'px';
				break;
			case 'bottom-end':
				elementNewY += arrowHeight;
				// Move arrow to the horizontal middle of the anchor
				arrow.style.right =
					anchor.offsetWidth / 2 - arrowWidth / 2 + 'px';
				arrow.style.top = -(arrowHeight / 2) + 'px';
				break;
			default:
				throw new Error(
					'Invalid canvas object relative position placement'
				);
		}
	}

	element.style.top = elementNewY + 'px';
	element.style.left = elementNewX + 'px';

	return { x: elementNewX, y: elementNewY };
}

export function getElementBoundingBoxInCanvasCoordinates(
	element: HTMLElement,
	canvasElement: HTMLElement,
	scale?: number
): IArea {
	// Parse the canvas element's transform style property
	const rawTransform = canvasElement.style.transform;
	const canvasScale = scale || parseFloat(rawTransform.split('(')[1]) || 1;

	const parentCanvasElement: HTMLElement = getCanvasParentElement(
		element,
		canvasElement
	);

	const coords = getElementCanvasCoordinates(
		element,
		parentCanvasElement,
		canvasScale
	);

	return {
		top: coords.top,
		left: coords.left,
		right: coords.right,
		bottom: coords.bottom
	};
}

export class Connection {
	id: string;
	canvas: Canvas;
	sourceParentObject: DraggableObject | CanvasObject;
	targetParentObject: DraggableObject | CanvasObject;
	startId: string;
	endId: string;
	source: HTMLElement;
	target: HTMLElement;
	selected: boolean = false;
	disabled: boolean = false;
	deleted: boolean = false;

	// d3 instances
	svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
	path: d3.Selection<SVGPathElement, unknown, HTMLElement, any>; // Hit Detection
	complementaryPath: d3.Selection<SVGPathElement, unknown, HTMLElement, any>; // Visible grey line or main standard line
	marker: d3.Selection<SVGMarkerElement, unknown, HTMLElement, any>;
	markerId: string;

	// Group for true custom SVG Chevrons
	pathId: string;
	chevronGroup: d3.Selection<SVGGElement, unknown, HTMLElement, any>;

	// Options
	initialOptions: any;

	classes: string[] = [];
	size: number;
	color: string;
	selectedColor: string;
	complementaryColor?: string;
	dash?: boolean | { animation: boolean };
	hoverColor?: string;

	hovering: boolean = false;
	transitioning: boolean = false;

	hoverTimeout: ReturnType<typeof setTimeout>;
	hasSubscribedEvents: boolean = false;

	constructor(
		source: HTMLElement,
		startId: string,
		target: HTMLElement,
		endId: string,
		options: any,
		canvas: Canvas,
		sourceParentObject: DraggableObject | CanvasObject,
		targetParentObject: DraggableObject | CanvasObject,
		private readonly debug: boolean = false
	) {
		this.onMouseOver = this.onMouseOver.bind(this);
		this.onMouseLeave = this.onMouseLeave.bind(this);
		this.onClick = this.onClick.bind(this);

		this.id = Connection.getId(startId, endId);
		this.sourceParentObject = sourceParentObject;
		this.targetParentObject = targetParentObject;
		this.canvas = canvas;
		this.startId = startId;
		this.endId = endId;
		this.source = source;
		this.target = target;

		const config = Connection.withDefaultOptions(options);

		this.initialOptions = config;

		this.color = config.color;
		this.size = config.size;
		this.dash = config.dash;
		this.complementaryColor = config.complementaryColor;
		this.hoverColor = config.hoverColor;
		this.classes = config.classes;

		this.init();
	}

	static setDefaults(options: IConnectionOptions) {
		Connection.optionsDefaults = Object.assign(
			Connection.optionsDefaults,
			options
		);
	}

	static optionsDefaults: IConnectionOptions = {
		size: 3,
		color: 'grey',
		complementaryColor: 'lightgrey',
		hoverColor: 'purple',
		onClick: (connection: Connection) => {
			Logger.log('clicked connection', connection);
		},
		onUnselect: (connection: Connection) => {
			Logger.log('unselected connection', connection);
		},
		onEnable: (connection: Connection) => {
			Logger.log('enabled connection', connection);
		},
		onDisable: (connection: Connection) => {
			Logger.log('disabled connection', connection);
		},
		classes: []
	};

	static withDefaultOptions(options: IConnectionOptions): IConnectionOptions {
		const { complementaryColor: cmpColor, ...sanitizedOptions } = options;

		const size = options.size || Connection.optionsDefaults.size;
		const color = options.color || Connection.optionsDefaults.color;
		const complementaryColor =
			cmpColor || Connection.optionsDefaults.complementaryColor;

		const formattedOptions: IConnectionOptions = {
			...sanitizedOptions,
			color,
			size
		};

		return {
			...formattedOptions,
			complementaryColor,
			classes: options.classes || []
		};
	}

	get element(): SVGElement {
		return this.svg.node() as SVGElement;
	}

	get markerElement(): SVGMarkerElement {
		return this.marker.node() as SVGMarkerElement;
	}

	get lineElement(): SVGPathElement {
		return this.path.node() as SVGPathElement;
	}

	get complementaryLineElement(): SVGPathElement | null {
		return (this.complementaryPath?.node() as SVGPathElement) || null;
	}

	init() {
		this.setupElements();
		this.update();
	}

	setupElements() {
		if (this.deleted) return;

		this.svg = d3
			.select('#' + this.canvas.id)
			.insert('svg', ':first-child')
			.attr('id', 'connection-svg--' + this.id)
			.style('position', 'absolute')
			.style('pointer-events', 'none');

		this.svg.node().classList.add('connector-line', ...this.classes);

		this.generateMarker();

		const randomStr = Math.random().toString(36).substring(7);
		this.pathId = `main-path--${this.id}--${randomStr}`;

		// Support line (Visible Grey Line OR Main solid line) goes on bottom
		this.complementaryPath = this.svg.append('path');

		// Invisible Hit Detection Path goes over it
		this.path = this.svg.append('path').attr('id', this.pathId);

		// Group for our custom chevrons
		this.chevronGroup = this.svg
			.append('g')
			.style('pointer-events', 'none');

		this.addEventsToElement(this.svg.node());

		if (!window.document.body.contains(this.svg.node())) {
			console.warn(
				'Connection svg element is not part of the document body'
			);
		}
	}

	reconcileDOM() {
		if (this.deleted) {
			this.remove();
			return;
		}

		if (!this.element) {
			this.setupElements();
		} else if (!window.document.body.contains(this.element)) {
			this.canvas.canvasElement.appendChild(this.element);
		}
	}

	update(debug?: boolean) {
		this.reconcileDOM();
		if (this.deleted) return;

		if (
			!this.source ||
			window.document.contains(this.source) === false ||
			Canvas.selectByAttribute(this.startId) !== this.source
		) {
			this.source = Canvas.selectByAttribute(this.startId) as HTMLElement;
		}

		if (
			!this.target ||
			window.document.contains(this.target) === false ||
			Canvas.selectByAttribute(this.endId) !== this.target
		) {
			this.target = Canvas.selectByAttribute(this.endId) as HTMLElement;
		}

		if (!this.source || !this.target) {
			this.element.remove();
			return;
		}

		const sourceCoords = getElementCanvasCoordinates(
			this.source,
			this.sourceParentObject.element,
			this.canvas.currentScale
		);
		const targetCoords = getElementCanvasCoordinates(
			this.target,
			this.targetParentObject.element,
			this.canvas.currentScale
		);

		const sourceHeight =
			this.source.offsetHeight || this.source.clientHeight;
		const targetHeight =
			this.target.offsetHeight || this.target.clientHeight;
		const width = targetCoords.left - sourceCoords.right;

		let placement: 'right' | 'right-start' | 'right-end' | 'left' = 'right';

		if (sourceCoords.top < targetCoords.top) placement = 'right-start';
		if (sourceCoords.top > targetCoords.top) placement = 'right-end';
		if (
			sourceCoords.top < targetCoords.top &&
			sourceCoords.bottom > targetCoords.bottom
		)
			placement = 'right';
		if (
			sourceCoords.top > targetCoords.top &&
			sourceCoords.bottom < targetCoords.bottom
		)
			placement = 'left';

		const top = Math.min(
			sourceCoords.top,
			targetCoords.top,
			sourceCoords.bottom,
			targetCoords.bottom
		);
		const bottom = Math.max(
			sourceCoords.top,
			targetCoords.top,
			sourceCoords.bottom,
			targetCoords.bottom
		);
		const absoluteElementsAreaHeight = bottom - top;

		const sourceCenterFromTop = sourceHeight / 2;
		const sourceCenterFromBottom =
			absoluteElementsAreaHeight - sourceHeight / 2;
		const targetCenterFromTop = targetHeight / 2;
		const targetCenterFromBottom =
			absoluteElementsAreaHeight - targetHeight / 2;

		let linkSourceY: number = sourceCenterFromTop;
		let linkTargetY: number = targetCenterFromTop;

		const markerHeight = 12;
		const calculatedSvgCanvasHeight =
			absoluteElementsAreaHeight + markerHeight * 2;

		if (sourceCoords.top < targetCoords.top) {
			linkSourceY = sourceCenterFromTop;
			linkTargetY = targetCenterFromBottom;
		}
		if (sourceCoords.top > targetCoords.top) {
			linkSourceY = sourceCenterFromBottom;
			linkTargetY = targetCenterFromTop;
		}
		if (
			sourceCoords.top < targetCoords.top &&
			sourceCoords.bottom > targetCoords.bottom
		) {
			linkSourceY = sourceCenterFromTop;
			linkTargetY =
				targetCenterFromTop + (targetCoords.top - sourceCoords.top);
		}
		if (
			sourceCoords.top > targetCoords.top &&
			sourceCoords.bottom < targetCoords.bottom
		) {
			linkSourceY =
				sourceCenterFromTop + (sourceCoords.top - targetCoords.top);
			linkTargetY = targetCenterFromTop;
		}

		linkTargetY += markerHeight;
		linkSourceY += markerHeight;

		this.svg.attr('width', width).attr('height', calculatedSvgCanvasHeight);

		const newCoords = positionRelativeTo(
			this.element as any as HTMLElement,
			placement.startsWith('left') ? this.target : this.source,
			placement.startsWith('left')
				? this.targetParentObject.element
				: this.sourceParentObject.element,
			this.canvas.currentScale,
			placement
		);

		let resolvedY = newCoords.y;
		if (placement === 'right-start') resolvedY = newCoords.y - markerHeight;
		if (placement === 'right-end') resolvedY = newCoords.y + markerHeight;

		this.svg.style('top', resolvedY).style('left', newCoords.x);

		const linkSource = { x: 0, y: linkSourceY };
		const linkTarget = { x: width, y: linkTargetY };

		const link = d3
			.linkHorizontal<any, any, any>()
			.x((d: any) => d.x)
			.y((d: any) => d.y)({ source: linkSource, target: linkTarget });

		let resolvedStateColor = this.color;
		if (this.selected)
			resolvedStateColor = Connection.optionsDefaults.selectedColor;
		if (this.hovering)
			resolvedStateColor =
				this.hoverColor || Connection.optionsDefaults.hoverColor;

		this.marker
			.attr('stroke', resolvedStateColor)
			.attr('fill', resolvedStateColor)
			.select('path')
			.attr('stroke', resolvedStateColor)
			.attr('fill', resolvedStateColor);

		// FEATURE FLAG: Use `this.dash` to determine if this is a call-stack line or a value line
		const isChevronStyle = !!this.dash;

		// 1. Render the main/support line
		this.complementaryPath
			.attr('d', link)
			// If it's a chevron line, use grey. If it's standard, use the requested color
			.attr('stroke', isChevronStyle ? '#ccc' : resolvedStateColor)
			// If it's a chevron line, make it thick. If standard, use the requested size
			.attr('stroke-width', isChevronStyle ? 7 : this.size)
			.attr('fill', 'none')
			.attr('marker-end', `url(#${this.markerId})`);

		// 2. The hit-area path becomes pure, invisible utility
		this.path
			.attr('d', link)
			.attr('stroke-width', 20)
			.attr('stroke', 'transparent')
			.attr('fill', 'none')
			.style('pointer-events', 'stroke');

		// 3. Render Chevrons ONLY if the feature flag is true
		if (isChevronStyle) {
			// Ensure the group is visible
			this.chevronGroup.style('display', null);

			const CHEVRON_SPACING = 16;
			const pathNode = this.complementaryPath.node() as SVGPathElement;
			const pathLength = pathNode.getTotalLength() || 0;

			const numChevrons = Math.floor(pathLength / CHEVRON_SPACING) + 2;
			const chevronsData = Array.from({ length: numChevrons }).map(
				(_, i) => (i - 1) * CHEVRON_SPACING
			);

			const chevrons = this.chevronGroup
				.selectAll('g.chevron')
				.data(chevronsData);
			const chevronsEnter = chevrons
				.enter()
				.append('g')
				.attr('class', 'chevron');

			chevronsEnter
				.append('path')
				.attr('d', 'M -6 -3.5 L 6 -3.5 L 8 0 L 6 3.5 L -6 3.5 L -4 0 Z')
				.attr('stroke', 'none');

			chevrons.exit().remove();

			const allChevrons = chevronsEnter.merge(chevrons as any);

			allChevrons.select('path').attr('fill', resolvedStateColor);

			allChevrons.each(function (d: number) {
				d3.select(this).attr('data-base-length', d.toString());
			});

			this.positionChevrons(0);

			if (this.transitioning) {
				this.startAnimation();
			}
		} else {
			// Hide the group for value-to-value lines so nothing overlaps
			this.chevronGroup.style('display', 'none');
		}
	}

	positionChevrons(offset: number) {
		const pathNode = this.complementaryPath.node() as SVGPathElement;
		const pathLength = pathNode.getTotalLength() || 0;

		this.chevronGroup.selectAll('g.chevron').attr('transform', function () {
			const baseLen = parseFloat(
				d3.select(this).attr('data-base-length')
			);
			let targetLen = baseLen + offset;

			if (targetLen < 0 || targetLen > pathLength) {
				d3.select(this).attr('opacity', 0);
				return `translate(-100,-100)`;
			} else {
				d3.select(this).attr('opacity', 1);
			}

			const pt = pathNode.getPointAtLength(targetLen);
			const pt2 = pathNode.getPointAtLength(
				Math.min(targetLen + 1, pathLength)
			);
			let angle =
				Math.atan2(pt2.y - pt.y, pt2.x - pt.x) * (180 / Math.PI);

			return `translate(${pt.x}, ${pt.y}) rotate(${angle})`;
		});
	}

	subscribeEvents() {
		if (!this.hasSubscribedEvents)
			this.addEventsToRelevantSVGChildren(this.element);
		this.hasSubscribedEvents = true;
	}

	addEventsToElement(element: SVGElement) {
		element.style.pointerEvents = 'auto!important';
		element.addEventListener('click', this.onClick);
		element.addEventListener('mouseover', this.onMouseOver);
		element.addEventListener('mouseleave', this.onMouseLeave);
	}

	removeEventsFromElement(element: SVGElement) {
		element.removeEventListener('click', this.onClick);
		element.removeEventListener('mouseover', this.onMouseOver);
		element.removeEventListener('mouseleave', this.onMouseLeave);
	}

	addEventsToRelevantSVGChildren(
		element: SVGDefsElement | SVGGElement | SVGElement
	) {
		const children = element.children;
		for (let i = 0; i < children.length; i++) {
			const child = children[i];
			if (
				child instanceof SVGPathElement ||
				child instanceof SVGUseElement ||
				child instanceof SVGTextElement ||
				child instanceof SVGMarkerElement
			) {
				this.addEventsToElement(child);
			} else if (
				child instanceof SVGDefsElement ||
				child instanceof SVGGElement
			) {
				this.addEventsToRelevantSVGChildren(child);
			}
		}
	}

	onMouseLeave(e: MouseEvent) {
		if (!this.selected) {
			this.hoverTimeout = setTimeout(() => {
				this.color = this.initialOptions.color;
				clearTimeout(this.hoverTimeout);
				this.hoverTimeout = null;
			}, 200);

			this.color = this.initialOptions.color;
			this.hovering = false;
			this.update();
		}
		this.hovering = false;
	}

	onMouseOver(e: MouseEvent) {
		if (
			(!this.hoverColor && !Connection.optionsDefaults.hoverColor) ||
			this.disabled ||
			this.canvas.disabled
		)
			return;

		const isSelectingArea =
			this.canvas.isMultiselecting && this.canvas.hasDraggedClick;
		if (
			this.canvas.isSelecting ||
			isSelectingArea ||
			this.canvas.hasDraggedClick ||
			this.canvas.hasMovedDragged ||
			this.canvas.isMovingObjects ||
			this.selected
		)
			return;

		if (this.hoverTimeout) {
			clearTimeout(this.hoverTimeout);
			this.hoverTimeout = null;
		}

		this.color = this.hoverColor || Connection.optionsDefaults.hoverColor;
		this.hovering = true;
		this.update();
	}

	generateMarker(): d3.Selection<
		SVGMarkerElement,
		unknown,
		HTMLElement,
		any
	> {
		this.svg.select('defs').remove();

		// Check our feature flag to determine arrowhead size
		const isChevronStyle = !!this.dash;

		// Scale is 2.5 for chunky chevrons, 1.2 for standard value lines
		const scale = isChevronStyle ? 2.5 : 1.2;

		const markerBoxWidth = 12 * scale;
		const markerBoxHeight = 12 * scale;

		const refX = markerBoxWidth - (isChevronStyle ? 2 : 1);
		const refY = markerBoxHeight / 2;

		const markerWidth = markerBoxWidth;
		const markerHeight = markerBoxHeight;

		const arrowPoints: Iterable<[number, number]> = [
			[0, 0],
			[12 * scale, 6 * scale],
			[0, 12 * scale],
			[3 * scale, 6 * scale]
		];

		this.marker = this.svg.append('svg:defs').append('svg:marker');
		const randomId = Math.random().toString(36).substring(7);
		this.markerId = `arrow-head--${this.id}--${randomId}`;

		this.marker
			.attr('id', this.markerId)
			.attr('viewBox', `0 0 ${markerBoxWidth} ${markerBoxHeight}`)
			.attr('refX', refX)
			.attr('refY', refY)
			.attr('markerUnits', 'userSpaceOnUse')
			.attr('markerWidth', markerWidth)
			.attr('markerHeight', markerHeight)
			.attr('orient', 'auto')
			.append('path')
			.attr('d', d3.line()(arrowPoints));

		return this.marker;
	}

	onClick(e: MouseEvent) {
		if (this.disabled || this.canvas.disabled) return;

		e.stopImmediatePropagation();

		if (this.canvas.isMultiselecting) {
			if (this.selected) {
				this.canvas.unselectConnection(this);
				return;
			}
			this.canvas.selectConnection(this);
			this.requestDeleteObject();
			return;
		} else {
			this.canvas.unselectAllConnections();
			if (this.selected) {
				this.canvas.unselectConnection(this);
				return;
			}
			this.canvas.selectConnection(this);
			this.requestDeleteObject();
			return;
		}
	}

	refresh(debug?: boolean) {
		if (this.deleted) return;
		this.update(debug);
	}

	static getId(startId: string, endId: string) {
		return `${startId}--${endId}`;
	}

	select() {
		this.selected = true;
		this.update();
	}

	unselect() {
		if (!this.selected) return;
		this.selected = false;
		this.color = this.initialOptions.color;
		this.update();
		Connection.optionsDefaults.onUnselect?.(this as any);
	}

	removeEventsFromRelevantSVGChildren(
		element: SVGDefsElement | SVGGElement | SVGElement
	) {
		const children = element.children;
		for (let i = 0; i < children.length; i++) {
			const child = children[i];
			if (
				child instanceof SVGPathElement ||
				child instanceof SVGUseElement ||
				child instanceof SVGTextElement ||
				child instanceof SVGMarkerElement
			) {
				this.removeEventsFromElement(child as SVGElement);
			} else if (
				child instanceof SVGDefsElement ||
				child instanceof SVGGElement
			) {
				this.removeEventsFromRelevantSVGChildren(child);
			}
		}
	}

	remove(options?: { force?: boolean; batchDelete?: boolean }): {
		deleted: boolean;
	} {
		const optionsWithDefaults = {
			...(options || {}),
			batchDelete: options?.batchDelete ?? true,
			force: options?.force ?? true
		};

		if (!optionsWithDefaults.force) {
			const sourceCheck =
				this.sourceParentObject.triggerBeforeDeleteConnection?.(
					this,
					optionsWithDefaults
				);
			if ((sourceCheck as { blockDelete?: boolean })?.blockDelete) {
				return { deleted: false };
			}

			if (
				!(sourceCheck as { skipTargetCheck: boolean })?.skipTargetCheck
			) {
				const targetCheck =
					this.targetParentObject.triggerBeforeDeleteConnection?.(
						this,
						optionsWithDefaults
					);
				if ((targetCheck as { blockDelete?: boolean })?.blockDelete) {
					return { deleted: false };
				}
			}
		}

		this.unselect();

		this.deleted = true;
		this.removeEventsFromElement(this.svg.node());
		this.removeEventsFromRelevantSVGChildren(this.svg.node());
		this.svg.remove();

		return { deleted: true };
	}

	startAnimation() {
		// Only animate if it's the chevron style!
		if (!this.dash) return;

		const CHEVRON_SPACING = 16;

		const animateLine = () => {
			this.chevronGroup
				.transition()
				.duration(600)
				.ease(d3.easeLinear)
				.tween('chevron-tween', () => {
					return (t: number) => {
						this.positionChevrons(t * CHEVRON_SPACING);
					};
				})
				.on('end', () => {
					if (this.transitioning) animateLine();
				});
		};

		this.transitioning = true;
		animateLine();
	}

	stopAnimation() {
		this.transitioning = false;
		this.chevronGroup.interrupt();
	}

	resetOptions() {
		this.dash = this.initialOptions.dash;
		this.color = this.initialOptions.color;
		this.size = this.initialOptions.size;
		this.complementaryColor = this.initialOptions.complementaryColor;
		this.hoverColor = this.initialOptions.hoverColor;
		this.update();
	}

	disable() {
		this.disabled = true;
		this.stopAnimation();
		Connection.optionsDefaults.onDisable(this as any);
	}

	enable() {
		if (!this.disabled || this.canvas.disabled) return;
		this.disabled = false;
		this.resetOptions();
		Connection.optionsDefaults.onEnable(this as any);
	}

	setDeleteObject(canvasObject: CanvasObject) {
		const userCustomElement = canvasObject.element;
		const parentTop = parseFloat(this.element.style.top);
		const parentLeft = parseFloat(this.element.style.left);
		const parentWidth =
			(this.element as any as HTMLElement).offsetWidth ||
			this.element.clientWidth;
		const parentHeight =
			(this.element as any as HTMLElement).offsetHeight ||
			this.element.clientHeight;
		const ownWidth =
			userCustomElement.offsetWidth || userCustomElement.clientWidth;
		const ownHeight =
			userCustomElement.offsetHeight || userCustomElement.clientHeight;

		const left = parentLeft + parentWidth / 2 - ownWidth / 2;
		const top = parentTop + parentHeight / 2 - ownHeight / 2;

		canvasObject.update({
			coordinates: { x: left, y: top - 6 }
		});
	}

	removeDeleteObject() {}

	requestDeleteObject() {
		Connection.optionsDefaults.onClick(this as any);
	}

	showDeleteElement() {
		let userCustomElement = Connection.optionsDefaults.onClick(this as any);
		if (!userCustomElement) {
			userCustomElement = document.createElement('div');
			userCustomElement.innerHTML = 'x';
			userCustomElement.style.color = 'red';
			userCustomElement.style.fontSize = '20px';
			userCustomElement.style.cursor = 'pointer';
		}

		userCustomElement.style.position = 'absolute';
		userCustomElement.style.transform = 'translateX(-50%)';

		const parentTop = parseFloat(this.element.style.top);
		const parentLeft = parseFloat(this.element.style.left);
		const parentWidth =
			(this.element as any as HTMLElement).offsetWidth ||
			this.element.clientWidth;
		const parentHeight =
			(this.element as any as HTMLElement).offsetHeight ||
			this.element.clientHeight;

		const left = parentLeft + parentWidth / 2;
		const top = parentTop + parentHeight / 2;

		userCustomElement.style.left = left + 'px';
		userCustomElement.style.top = top - 6 + 'px';

		this.canvas.canvasElement.appendChild(userCustomElement);
	}
}

export class BaseCanvasObject {
	element: HTMLElement;
	id: string;
	canvas: Canvas;

	constructor({ id, element, canvas }: IBaseCanvasObjectCreateOptions) {
		this.id = id;
		this.element = element;
		this.canvas = canvas;
	}
}

export class RelativeObjectsState extends BaseCanvasObject {
	relativeObjects: RelativeObject[] = [];

	// Creates a new relative object and stores it in its state
	addRelativeObject(config: {
		element: HTMLElement;
		id: string;
		anchor: HTMLElement;
		placement: Placement;
		arrowClasses?: string[];
		arrowElement?: HTMLElement;
		noArrow?: boolean;
		offset?: number;
		persisted?: boolean;
		arrowContainerSelector?: string;
	}): RelativeObject {
		const relativeObject = new RelativeObject({
			...config,
			relativeTo: this,
			canvas: this.canvas
		});

		this.relativeObjects.push(relativeObject);

		return relativeObject;
	}

	// toggles the visibility of the object on
	show() {
		this.element.style.display = 'revert';
	}

	// toggles the visibility of the object off
	hide() {
		this.element.style.display = 'none';

		// Loop over the relative objects and hide them as well
		this.relativeObjects.forEach((relObj) => {
			relObj.hide();
		});
	}

	removeRelativeObjectById(id: string) {
		this.relativeObjects = this.relativeObjects.filter((relativeObject) => {
			if (relativeObject.id === id) {
				// Recursively delete also own relative objects
				relativeObject.removeAllRelativeObjects();

				relativeObject.remove();
				return false;
			}

			return true;
		});
	}

	hideRelativeObjectById(id: string) {
		this.relativeObjects.forEach((relativeObject) => {
			if (relativeObject.id === id) {
				relativeObject.hide();
			}
		});
	}

	showRelativeObjectById(id: string) {
		this.relativeObjects.forEach((relativeObject) => {
			if (relativeObject.id === id) {
				relativeObject.show();
			}
		});
	}

	removeRelativeObject(relativeObject: RelativeObject) {
		this.relativeObjects = this.relativeObjects.filter((relObj) => {
			if (relObj === relativeObject) {
				// Recursively delete also own relative objects
				relObj.removeAllRelativeObjects();

				relObj.remove();
				return false;
			}

			return true;
		});
	}

	hideRelativeObject(relativeObject: RelativeObject) {
		this.relativeObjects.forEach((relObj) => {
			if (relObj === relativeObject) {
				relObj.hide();
			}
		});
	}

	showRelativeObject(relativeObject: RelativeObject) {
		this.relativeObjects.forEach((relObj) => {
			if (relObj === relativeObject) {
				relObj.show();
			}
		});
	}

	removeAllRelativeObjects() {
		this.relativeObjects.forEach((relativeObject) => {
			// Recursively delete also own relative objects
			relativeObject.removeAllRelativeObjects();

			relativeObject.remove();
		});

		this.relativeObjects = [];
	}

	hideAllRelativeObjects() {
		this.relativeObjects.forEach((relativeObject) => {
			relativeObject.hide();
		});
	}

	// Recursively check if the given relative object is in the state
	// or any of its nested children
	// Including itself
	containsRelativeObject(relativeObject: RelativeObject): boolean {
		if (this.relativeObjects.includes(relativeObject)) {
			return true;
		}

		return this.relativeObjects.some((relObj) => {
			return relObj.containsRelativeObject(relativeObject);
		});
	}

	/*
		Givem a relative object, remove all other relative objects and their children
		If a given object is a child of another, keep its parent, but remove all siblings
	*/
	removeAllRelativeObjectsExcept(relativeObject: RelativeObject): {
		hasFound: boolean;
	} {
		let hasFound = false;

		this.relativeObjects = this.relativeObjects.filter((relObj) => {
			if (relObj === relativeObject) {
				hasFound = true;
				return true;
			}

			const { hasFound: hasFoundInChildren } =
				relObj.removeAllRelativeObjectsExcept(relativeObject);

			if (hasFoundInChildren) {
				hasFound = true;
				return true;
			}

			relObj.removeAllRelativeObjects();
			return false;
		});

		return { hasFound };
	}

	hideAllRelativeObjectsExcept(relativeObject: RelativeObject): {
		hasFound: boolean;
	} {
		let hasFound = false;

		this.relativeObjects.forEach((relObj) => {
			if (relObj === relativeObject) {
				hasFound = true;
				return;
			}

			const { hasFound: hasFoundInChildren } =
				relObj.hideAllRelativeObjectsExcept(relativeObject);

			if (hasFoundInChildren) {
				hasFound = true;
				return;
			}

			if (!relObj.persisted) {
				relObj.hide();
			}
		});

		return { hasFound };
	}

	recalculateRelativeObjects() {
		this.relativeObjects.forEach((relativeObject) => {
			relativeObject.calculatePosition();
		});
	}
}

export class AbsoluteCanvasObject extends RelativeObjectsState {
	x: number;
	y: number;

	constructor({
		id,
		element,
		canvas,
		coordinates
	}: IAbsoluteCanvasObjectCreateOptions) {
		super({ id, element, canvas });
		this.id = id;
		this.element = element;
		this.canvas = canvas;
		this.x = coordinates.x;
		this.y = coordinates.y;
	}
}

export class CanvasObject extends AbsoluteCanvasObject {
	connections: Connection[] = [];
	x: number;
	y: number;
	selectable: boolean = false;
	selected: boolean = false;
	deletable: boolean = false;
	disabled: boolean = false;
	deleted: boolean = false;

	// We store the last known element height to calculate the new y position
	// So it doesn't jump when the element grows
	// This value is recalculated every time the element is resized
	lastHeight: number = 0;

	updateCallback: null | NonDraggableOnUpdateCallback = null;
	onBeforeDeleteCallback: null | (() => void) = null;
	onBeforeDeleteConnectionCallback: null | DraggableBeforeDeleteConnectionCallback =
		null;
	beforeUpdateCallback: null | NonDraggableBeforeUpdateCallback = null;
	onRestoreCallback: null | (() => void) = null;

	private _resizeObserver: ResizeObserver;

	constructor({
		id,
		coordinates,
		element,
		canvas,
		selectable,
		deletable
	}: ICanvasObjectCreateOptions) {
		super({ id, element, canvas, coordinates });
		this.selectable = selectable || false;
		this.deletable = deletable || false;

		this._resizeObserver = new ResizeObserver(this._boundResizeHandler);

		this.setup();
	}

	_boundResizeHandler = () => this.resize();

	// Refreshes the DOM by re-adding its element and all its children and dependent object to the canvas element
	refresh() {
		this.canvas.canvasElement.appendChild(this.element);

		this.relativeObjects.forEach((relativeObject) => {
			relativeObject.refresh();
		});

		this.connections.forEach((connection) => {
			connection.refresh();
		});
	}

	// This function is not meant to determin if the connection is the same instance
	// but if the two connections are equivalent, therefore it can be used to skip stabilizing new redundant connections
	static isEquivalentConnection(
		connectionA: IConnectionValue,
		connectionB: IConnectionValue
	): boolean {
		if (connectionA === connectionB) {
			return true;
		}

		if (
			connectionA.startId == connectionB.startId &&
			connectionA.endId === connectionB.endId
		) {
			// Check if the options are equal
			// All properties of the 'initialOptions' object should be equal to their counterpart in the other connection
			// The 'complementaryColor' property should also be equal
			for (const key in connectionA.initialOptions) {
				if (
					// @ts-ignore
					connectionA.initialOptions[key] !==
					// @ts-ignore
					connectionB.initialOptions[key]
				) {
					return false;
				}
			}

			return true;
		}

		return false;
	}

	updateConnection(connection: Connection): Connection {
		connection.update();

		return connection;
	}

	updateConnections() {
		// Update the connections
		this.connections = this.connections.filter((conn) => {
			const counterpart =
				conn.sourceParentObject === this
					? conn.targetParentObject
					: conn.sourceParentObject;

			if (counterpart.deleted) {
				this.removeConnection(conn);
				return false;
			}

			this.updateConnection(conn);
			return true;
		});
	}

	resize() {
		// Store the last known height of the element
		// Then get the new height and calculate the difference
		// Then add the difference to the y position
		const previousHeight = this.lastHeight;
		this.lastHeight = this.element.offsetHeight;

		const difference = this.lastHeight - previousHeight;
		const yChange = difference !== 0 ? difference / 2 : 0;

		if (yChange === 0) {
			return;
		}

		// Add the difference to the y position
		// Otherwise if the element grows, it jumps up
		this.y = this.y + yChange;

		this.element.style.top = `${this.y}px`;

		this.updateConnections();
		this.recalculateRelativeObjects();
	}

	setup() {
		this._resizeObserver.observe(this.element);

		this.element.id = this.id;
		// Add the custom canvas attribute name to the element
		this.element.setAttribute(Canvas.OBJ_ATTR_NAME, this.id);

		// Set up absolute positioning and starting coordinates
		this.element.style.position = 'absolute';
		this.element.style.left = `${this.x}px`;
		this.element.style.top = `${this.y}px`;

		// Add stope event propagation to the element so the canvas doesn't react to clicks on the cards
		// this.element.addEventListener('click', (e) => {
		// 	// e.stopImmediatePropagation();
		// });
	}

	update(
		config: IUpdateDraggableConfig,
		{ skipOverlapCheck }: { skipOverlapCheck: boolean } = {
			skipOverlapCheck: false
		}
	) {
		// Skip update when deleted
		if (this.deleted && config.deleted !== false) {
			return;
		}

		let modified = config;

		let overlaps: OverlapReport = [];

		if (config.coordinates && !skipOverlapCheck) {
			// Check if it overlaps before applying the movement
			overlaps = this.canvas.getObjectOverlaps(this, config.coordinates);
		}

		if (this.beforeUpdateCallback) {
			modified = this.beforeUpdateCallback({
				coordinates: {
					x: config.coordinates?.x || this.x,
					y: config.coordinates?.y || this.y
				},
				deleted: config.deleted,
				element: config.element || this.element,
				selected: this.selected,
				overlaps,
				newConnections: config.newConnections,
				deletedConnections: config.deletedConnections
			});
		}

		if (!this.deleted && !!modified.deleted) {
			this.remove();
			return;
		}

		let shouldUpdateConnections: boolean = false;

		if (modified.element) {
			shouldUpdateConnections = true;

			this.element = modified.element;

			// Apply old coordinates to new element
			this.setup();
		}

		if (modified.coordinates) {
			this.x = modified.coordinates.x;
			this.y = modified.coordinates.y;
			this.element.style.left = `${this.x}px`;
			this.element.style.top = `${this.y}px`;

			this.recalculateRelativeObjects();

			shouldUpdateConnections = true;
		}

		if (modified.newConnections?.length) {
			modified.newConnections.forEach((connection) => {
				const startElement = Canvas.selectByAttribute(
					connection.startId
				) as HTMLElement;
				const endElement = Canvas.selectByAttribute(
					connection.endId
				) as HTMLElement;

				if (!startElement || !endElement) {
					return;
				}

				let currentObjectIsSource =
					this.id === connection.sourceParentObject.objectId;

				let counterpartObject: DraggableObject =
					this.canvas.draggableObjects.find((obj) =>
						currentObjectIsSource
							? obj.id === connection.targetParentObject.objectId
							: obj.id === connection.sourceParentObject.objectId
					);

				// If the counterpart object is not found, it means it has been deleted or hasn't been restored yet
				if (!counterpartObject) {
					return;
				}

				const sourceParentObject = currentObjectIsSource
					? this
					: counterpartObject;
				const targetParentObject = currentObjectIsSource
					? counterpartObject
					: this;

				this.canvas.connect(
					startElement,
					connection.startId,
					endElement,
					connection.endId,
					sourceParentObject,
					targetParentObject,
					connection.initialOptions
				);
			});
		}

		if (modified.deletedConnections?.length) {
			this.connections = this.connections.filter((connection) => {
				const hasConnectionBeenDeleted =
					modified.deletedConnections.some((deletedConn) => {
						return deletedConn.id === connection.id;
					});

				if (hasConnectionBeenDeleted) {
					connection.remove();
					// Delete connection on the counterpart object
					const counterpartObject =
						connection.sourceParentObject === this
							? connection.targetParentObject
							: connection.sourceParentObject;

					counterpartObject.connections =
						counterpartObject.connections.filter(
							(counterpartConnection) =>
								connection !== counterpartConnection
						);

					return false;
				}

				return true;
			});
		}

		if (shouldUpdateConnections) {
			// Update the connections
			this.updateConnections();
		}

		if (this.updateCallback) {
			this.updateCallback({
				coordinates: {
					x: modified.coordinates?.x || this.x,
					y: modified.coordinates?.y || this.y
				},
				element: modified.element || this.element,
				selected: this.selected,
				overlaps,
				newConnections: config.newConnections,
				deletedConnections: config.deletedConnections
			});
		}
	}

	onUpdate(callback: DraggableOnUpdateCallback) {
		this.updateCallback = callback;
	}

	offUpdate() {
		this.updateCallback = null;
	}

	onBeforeDelete(callback: () => void) {
		this.onBeforeDeleteCallback = callback;
	}

	offBeforeDelete() {
		this.onBeforeDeleteCallback = null;
	}

	onBeforeDeleteConnection(
		callback: DraggableBeforeDeleteConnectionCallback
	) {
		this.onBeforeDeleteConnectionCallback = callback;
	}

	offBeforeDeleteConnection() {
		this.onBeforeDeleteConnectionCallback = null;
	}

	triggerBeforeDeleteConnection(
		connection: Connection,
		config: {
			batchDelete: boolean;
		}
	): { blockDelete?: boolean } | void {
		if (this.onBeforeDeleteConnectionCallback) {
			return this.onBeforeDeleteConnectionCallback(connection, config);
		}
	}

	onRestore(callback: () => void) {
		this.onRestoreCallback = callback;
	}

	offRestore() {
		this.onRestoreCallback = null;
	}

	// If the user undid a change where this entity was previously deleted and it need to be re-added to the canvas
	restore(config?: IUpdateDraggableConfig) {
		if (this.onRestoreCallback) {
			this.onRestoreCallback();
		}

		this.update({
			coordinates: {
				x: config.coordinates?.x || this.x,
				y: config.coordinates?.y || this.y
			},
			deleted: false,
			element: config.element || this.element,
			// All the connections are new when restoring an object
			newConnections:
				config.newConnections ||
				this.canvas.connectionsToUndoableValues(this.connections),
			deletedConnections: config.deletedConnections
		});
	}

	subscribeConnection(connection: Connection) {
		if (!this.connections.includes(connection)) {
			this.connections.push(connection);
		}
	}

	remove() {
		if (!this.deletable) {
			return;
		}

		if (this.onBeforeDeleteCallback) {
			this.onBeforeDeleteCallback();
		}

		// Remove all connections and their elements
		this.connections.forEach((connection) => {
			connection.remove({ force: true, batchDelete: true });

			// For each connection, remove it also from the counterpart element that it is linked to
			const counterpartObject =
				connection.sourceParentObject === this
					? connection.targetParentObject
					: connection.sourceParentObject;
			const counterpartConnections = counterpartObject.connections;

			// Remove the connection from the counterpart object
			const updatedConnections = counterpartConnections.filter(
				(counterpartConnection) => connection !== counterpartConnection
			);

			counterpartObject.connections = updatedConnections;
		});

		this.connections = [];

		this.removeAllRelativeObjects();
		this._resizeObserver.disconnect();

		this.element?.remove();

		this.deleted = true;
	}

	select() {
		if (this.disabled || this.canvas.disabled) {
			return;
		}

		if (!this.selectable) {
			return;
		}

		this.selected = true;

		if (this.updateCallback) {
			this.updateCallback({
				element: this.element,
				coordinates: {
					x: this.x,
					y: this.y
				},
				selected: this.selected,
				// TODO: Should we calculate the overlaps? Or reuse a previous calculation?
				overlaps: []
			});
		}
	}

	unselect() {
		if (!this.selectable) {
			this.selected = false;
			return;
		}

		if (this.selected && this.updateCallback) {
			this.selected = false;

			this.updateCallback({
				element: this.element,
				coordinates: {
					x: this.x,
					y: this.y
				},
				selected: this.selected,
				// TODO: Should we calculate the overlaps? Or reuse a previous calculation?
				overlaps: []
			});
		}
	}

	removeConnection(connection: Connection) {
		this.connections = this.connections.filter(
			(conn) => conn !== connection
		);
		connection.remove();

		const counterpartObject =
			connection.sourceParentObject === this
				? connection.targetParentObject
				: connection.sourceParentObject;
		const counterpartConnections = counterpartObject.connections;

		// Remove the connection from the counterpart object
		const updatedConnections = counterpartConnections.filter(
			(counterpartConnection) => connection !== counterpartConnection
		);

		counterpartObject.connections = updatedConnections;
	}

	removeAllConnections() {
		this.connections.forEach((connection) => {
			connection.remove();

			const counterpartObject =
				connection.sourceParentObject === this
					? connection.targetParentObject
					: connection.sourceParentObject;
			const counterpartConnections = counterpartObject.connections;

			// Remove the connection from the counterpart object
			const updatedConnections = counterpartConnections.filter(
				(counterpartConnection) => connection !== counterpartConnection
			);

			counterpartObject.connections = updatedConnections;
		});

		this.connections = [];
	}
}

// Popups that are anchored to the position of another object
export class RelativeObject extends RelativeObjectsState {
	offset: number = 0;

	// Arrow
	noArrow: boolean = false;
	arrowClasses: string[] = [];
	arrowElement: HTMLElement;
	arrowContainerSelector: string | null = null;

	// The object that this object is relative to
	relativeTo: BaseCanvasObject;
	// The specific anchor element of the relativeTo object
	anchor: HTMLElement;
	placement: Placement = 'right';

	/*
		This flag indicates where it should be automatically closed as a side effect of any other relative object
		This side effect is often used to close all currently visible objects when a new one is opened
		If set as 'true' this object can only be closed manually,
		by calling the 'hide' or 'remove' method on its instance directly
	*/
	persisted: boolean = false;

	onBeforeDeleteCallback: null | (() => void) = null;

	constructor({
		id,
		element,
		canvas,
		relativeTo,
		anchor,
		placement,
		arrowElement,
		noArrow = false,
		arrowClasses = [],
		offset = 0,
		persisted = false,
		arrowContainerSelector = null
	}: IBaseCanvasObjectCreateOptions & {
		relativeTo: BaseCanvasObject;
		anchor: HTMLElement;
		placement: Placement;
		arrowElement?: HTMLElement;
		noArrow?: boolean;
		arrowClasses?: string[];
		offset?: number;
		persisted?: boolean;
		arrowContainerSelector?: string;
	}) {
		super({ id, element, canvas });
		this.relativeTo = relativeTo;
		this.anchor = anchor;
		this.placement = placement || 'right';
		this.noArrow = noArrow;
		this.arrowClasses = arrowClasses;
		this.offset = offset || 0;
		this.arrowElement = arrowElement;
		this.persisted = persisted;
		this.arrowContainerSelector = arrowContainerSelector;

		this.setup();
	}

	// Refreshes the DOM by re-adding its element and all its children and dependent object to the canvas element
	refresh() {
		this.canvas.canvasElement.appendChild(this.element);

		this.relativeObjects.forEach((relativeObject) => {
			relativeObject.refresh();
		});
	}

	// toggles the visibility of the object on
	show() {
		super.show();

		if (this.arrowElement) {
			this.arrowElement.style.display = 'revert';
		}
	}

	// toggles the visibility of the object off
	hide() {
		super.hide();

		if (this.arrowElement) {
			this.arrowElement.style.display = 'none';
		}
	}

	setup() {
		this.element.id = this.id;
		// Add the custom canvas attribute name to the element
		this.element.setAttribute(Canvas.OBJ_ATTR_NAME, this.id);

		// Set up absolute positioning and starting coordinates
		this.element.style.position = 'absolute';

		// Add stope event propagation to the element so the canvas doesn't react to clicks on the cards
		// this.element.addEventListener('click', (e) => {
		// 	// e.stopImmediatePropagation();
		// });

		this.setupArrowElement();

		// Add the element to the canvas if not already there
		if (!this.canvas.canvasElement.contains(this.element)) {
			this.canvas.canvasElement.appendChild(this.element);
		}

		// Calculate the position of the element
		this.calculatePosition();
	}

	setupArrowElement() {
		if (!this.noArrow && !this.arrowElement) {
			// Create arrow element
			const arrowElement = document.createElement('div');

			// Add id to the element
			arrowElement.id = 'popup-arrow--' + this.id;

			// Add custom classes to the arrow
			this.arrowClasses.forEach((cls) => {
				arrowElement.classList.add(cls);
			});

			this.arrowElement = arrowElement;
		}

		const arrowParentContainer =
			(this.arrowContainerSelector &&
				document.querySelector(this.arrowContainerSelector)) ||
			this.element;

		// If the arrow element is not in the main element, append it
		if (!arrowParentContainer.contains(this.arrowElement)) {
			arrowParentContainer.appendChild(this.arrowElement);

			// Else if it contains the arrow, move it to the end
			// so it renders over previous children
		} else if (arrowParentContainer.contains(this.arrowElement)) {
			arrowParentContainer.appendChild(this.arrowElement);
		}

		// Make position absolute
		this.arrowElement.style.position = 'absolute';
	}

	onBeforeDelete(callback: () => void) {
		this.onBeforeDeleteCallback = callback;
	}

	offBeforeDelete() {
		this.onBeforeDeleteCallback = null;
	}

	teardownCallbacks() {
		this.offBeforeDelete();
	}

	remove() {
		if (this.onBeforeDeleteCallback) {
			this.onBeforeDeleteCallback();
		}

		this.removeAllRelativeObjects();
		// this.teardownCallbacks();

		if (
			this.element.parentElement &&
			this.element.parentElement.contains(this.element)
		) {
			this.element.parentElement.removeChild(this.element);
		}

		if (
			this.arrowElement.parentElement &&
			this.arrowElement.parentElement.contains(this.arrowElement)
		) {
			this.arrowElement.parentElement.removeChild(this.arrowElement);
		}
	}

	// Using canvas relative positions based on scale and croll,
	// calculate and set own x and y coordinates to be placed next to the anchor element
	calculatePosition() {
		positionRelativeTo(
			this.element,
			this.anchor,
			this.relativeTo.element,
			this.canvas.currentScale,
			this.placement,
			this.arrowElement,
			this.offset
		);

		this.recalculateRelativeObjects();
	}
}

export class DraggableObject extends AbsoluteCanvasObject {
	connections: Connection[] = [];
	x: number;
	y: number;
	selectable: boolean = true;
	selected: boolean = false;
	deletable: boolean = true;
	disabled: boolean = false;
	deleted: boolean = false;
	// Some events are global, we need to keep track of whether the object is the target of the event
	isTargetOfMouseDown = false;
	// We need to distinguish clicks from drags, so we keep track of whether the user has dragged the object
	hasDraggedClick: boolean = false;

	// We store the last known element height to calculate the new y position
	// So it doesn't jump when the element grows
	// This value is recalculated every time the element is resized
	lastHeight: number = 0;

	// Callbacks
	updateCallback: null | DraggableOnUpdateCallback = null;
	beforeUpdateCallback: null | DraggableBeforeUpdateCallback = null;
	onBeforeDeleteCallback: null | (() => void) = null;
	onBeforeDeleteConnectionCallback: null | DraggableBeforeDeleteConnectionCallback =
		null;
	onDragEndCallback: null | DraggableOnUpdateCallback = null;
	onDoubleClickCallback: null | ((e: MouseEvent) => void) = null;
	onRestoreCallback: null | (() => void) = null;
	onScrollToThisCallback: null | (() => void) = null;
	onOverlapStartCallback: null | ((overlappingWith: Overlaps) => void) = null;
	onOverlapEndCallback: null | ((overlappingWith: Overlaps) => void) = null;
	onDisableCallback: null | (() => void) = null;
	onEnableCallback: null | (() => void) = null;

	private _resizeObserver: ResizeObserver;

	constructor({
		id,
		coordinates,
		element,
		canvas,
		selectable,
		deletable
	}: ICanvasObjectCreateOptions) {
		super({ id, element, canvas, coordinates });
		this.selectable = selectable !== false ? true : false;
		this.deletable = deletable !== false ? true : false;
		this.hasDraggedClick = false;

		this.updateConnection = this.updateConnection.bind(this);
		this.resize = this.resize.bind(this);
		this._resizeObserver = new ResizeObserver(this._boundResizeHandler);

		this.setup();
	}

	// Refreshes the DOM by re-adding its element and all its children and dependent object to the canvas element
	refresh() {
		this.canvas.canvasElement.appendChild(this.element);

		this.relativeObjects.forEach((relativeObject) => {
			relativeObject.refresh();
		});

		this.connections.forEach((connection) => {
			connection.refresh();
		});
	}

	// This function is not meant to determin if the connection is the same instance
	// but if the two connections are equivalent, therefore it can be used to skip stabilizing new redundant connections
	static isEquivalentConnection(
		connectionA: IConnectionValue,
		connectionB: IConnectionValue
	): boolean {
		if (
			connectionA.startId === connectionB.startId &&
			connectionA.endId === connectionB.endId
		) {
			// Check if the options are equal
			// All properties of the 'initialOptions' object should be equal to their counterpart in the other connection
			// The 'complementaryColor' property should also be equal
			// for (const key in connectionA.initialOptions) {
			// 	if (
			// 		// @ts-ignore
			// 		connectionA.initialOptions[key] !== connectionB.initialOptions[key]
			// 	) {
			// 		return false;
			// 	}
			// }

			return true;
		}

		return false;
	}

	updateConnection(connection: Connection): Connection {
		connection.update();

		return connection;
	}

	updateConnections() {
		// Update the connections
		this.connections = this.connections.filter((conn) => {
			const counterpart =
				conn.sourceParentObject === this
					? conn.targetParentObject
					: conn.sourceParentObject;

			if (counterpart.deleted) {
				this.removeConnection(conn);
				return false;
			}

			this.updateConnection(conn);
			return true;
		});
	}

	update(
		config: IUpdateDraggableConfig,
		{
			skipOverlapCheck,
			quiet
		}: { skipOverlapCheck: boolean; quiet?: boolean } = {
			skipOverlapCheck: false,
			quiet: false
		}
	) {
		// Skip update when deleted
		if (this.deleted && config.deleted !== false) {
			return;
		}

		let modified = config;

		let overlaps: OverlapReport = [];

		if (config.coordinates && !skipOverlapCheck) {
			// Check if it overlaps before applying the movement
			overlaps = this.canvas.getObjectOverlaps(this, config.coordinates);
		}

		if (this.beforeUpdateCallback && !quiet) {
			modified = this.beforeUpdateCallback({
				coordinates: {
					// If the coordinates are not provided AND they are not 0, use the current ones
					x:
						config.coordinates?.x ||
						(config.coordinates?.x === 0
							? config.coordinates?.x
							: this.x),
					y:
						config.coordinates?.y ||
						(config.coordinates?.y === 0
							? config.coordinates?.y
							: this.y)
				},
				deleted: config.deleted,
				element: config.element || this.element,
				selected: this.selected,
				overlaps,
				newConnections: config.newConnections,
				deletedConnections: config.deletedConnections
			});
		}

		if (!this.deleted && !!modified.deleted) {
			this.remove();
			return;
		}

		let shouldUpdateConnections: boolean = false;

		if (modified.element) {
			shouldUpdateConnections = true;

			this.element = modified.element;

			// Apply old coordinates to new element
			this.setup();
		}

		if (modified.coordinates) {
			this.x = modified.coordinates.x;
			this.y = modified.coordinates.y;
			this.element.style.left = `${this.x}px`;
			this.element.style.top = `${this.y}px`;

			this.recalculateRelativeObjects();

			shouldUpdateConnections = true;
		}

		if (modified.newConnections?.length) {
			modified.newConnections.forEach((connection) => {
				const startElement = Canvas.selectByAttribute(
					connection.startId
				) as HTMLElement;
				const endElement = Canvas.selectByAttribute(
					connection.endId
				) as HTMLElement;

				if (!startElement || !endElement) {
					return;
				}

				let currentObjectIsSource =
					this.id === connection.sourceParentObject.objectId;

				let counterpartObject: DraggableObject =
					this.canvas.draggableObjects.find((obj) =>
						currentObjectIsSource
							? obj.id === connection.targetParentObject.objectId
							: obj.id === connection.sourceParentObject.objectId
					);

				// If the counterpart object is not found, it means it has been deleted or hasn't been restored yet
				if (!counterpartObject) {
					return;
				}

				const sourceParentObject = currentObjectIsSource
					? this
					: counterpartObject;
				const targetParentObject = currentObjectIsSource
					? counterpartObject
					: this;

				this.canvas.connect(
					startElement,
					connection.startId,
					endElement,
					connection.endId,
					sourceParentObject,
					targetParentObject,
					connection.initialOptions
				);
			});
		}

		if (modified.deletedConnections?.length) {
			this.connections = this.connections.filter((connection) => {
				const hasConnectionBeenDeleted =
					modified.deletedConnections.some((deletedConn) => {
						return deletedConn.id === connection.id;
					});

				if (hasConnectionBeenDeleted) {
					connection.remove();
					// Delete connection on the counterpart object
					const counterpartObject =
						connection.sourceParentObject === this
							? connection.targetParentObject
							: connection.sourceParentObject;

					counterpartObject.connections =
						counterpartObject.connections.filter(
							(counterpartConnection) =>
								connection !== counterpartConnection
						);

					return false;
				}

				return true;
			});
		}

		if (shouldUpdateConnections) {
			// Update the connections
			this.updateConnections();
		}

		if (this.updateCallback && !quiet) {
			this.updateCallback({
				coordinates: {
					x: modified.coordinates?.x || this.x,
					y: modified.coordinates?.y || this.y
				},
				element: modified.element || this.element,
				selected: this.selected,
				overlaps,
				newConnections: config.newConnections,
				deletedConnections: config.deletedConnections
			});
		}
	}

	onBeforeUpdate(callback: DraggableOnUpdateCallback) {
		this.beforeUpdateCallback = callback;
	}

	offBeforeUpdate() {
		this.beforeUpdateCallback = null;
	}

	onUpdate(callback: DraggableOnUpdateCallback) {
		this.updateCallback = callback;
	}

	offUpdate() {
		this.updateCallback = null;
	}

	onBeforeDelete(callback: () => void) {
		this.onBeforeDeleteCallback = callback;
	}

	offBeforeDelete() {
		this.onBeforeDeleteCallback = null;
	}

	onBeforeDeleteConnection(
		callback: DraggableBeforeDeleteConnectionCallback
	) {
		this.onBeforeDeleteConnectionCallback = callback;
	}

	offBeforeDeleteConnection() {
		this.onBeforeDeleteConnectionCallback = null;
	}

	triggerBeforeDeleteConnection(
		connection: Connection,
		config: {
			batchDelete: boolean;
		}
	): { blockDelete?: boolean; skipTargetCheck?: boolean } | void {
		if (this.onBeforeDeleteConnectionCallback) {
			return this.onBeforeDeleteConnectionCallback(connection, config);
		}
	}

	onDragEnd(callback: DraggableOnUpdateCallback) {
		this.onDragEndCallback = callback;
	}

	offDragEnd() {
		this.onDragEndCallback = null;
	}

	onDoubleClick(callback: (e: MouseEvent) => void) {
		this.onDoubleClickCallback = callback;
	}

	onRestore(callback: () => void) {
		this.onRestoreCallback = callback;
	}

	offRestore() {
		this.onRestoreCallback = null;
	}

	onScrollToThis(callback: () => void) {
		this.onScrollToThisCallback = callback;
	}

	offScrollToThis() {
		this.onScrollToThisCallback = null;
	}

	onOverlapStart(callback: (overlappingWith: Overlaps) => void) {
		this.onOverlapStartCallback = callback;
	}

	offOverlapStart() {
		this.onOverlapStartCallback = null;
	}

	onOverlapEnd(callback: (overlappingWith: Overlaps) => void) {
		this.onOverlapEndCallback = callback;
	}

	offOverlapEnd() {
		this.onOverlapEndCallback = null;
	}

	onDisable(callback: () => void) {
		this.onDisableCallback = callback;
	}

	offDisable() {
		this.onDisableCallback = null;
	}

	onEnable(callback: () => void) {
		this.onEnableCallback = callback;
	}

	offEnable() {
		this.onEnableCallback = null;
	}

	teardownCallbacks() {
		this.offBeforeUpdate();
		this.offUpdate();
		this.offBeforeDelete();
		this.offDragEnd();
		this.offRestore();
		this.offScrollToThis();
		this.offOverlapStart();
		this.offOverlapEnd();
		this.offDisable();
		this.offEnable();
	}

	// If the user undid a change where this entity was previously deleted and it need to be re-added to the canvas
	restore(config?: IUpdateDraggableConfig) {
		if (this.onRestoreCallback) {
			this.onRestoreCallback();
		}

		this.update({
			coordinates: {
				x: config.coordinates?.x || this.x,
				y: config.coordinates?.y || this.y
			},
			deleted: false,
			element: config.element || this.element,
			// All the connections are new when restoring an object
			newConnections:
				config.newConnections ||
				this.canvas.connectionsToUndoableValues(this.connections),
			deletedConnections: config.deletedConnections
		});
	}

	remove(removeOptions: { quiet: boolean } = { quiet: false }) {
		if (!this.deletable) {
			return;
		}

		if (this.onBeforeDeleteCallback && !removeOptions.quiet) {
			this.onBeforeDeleteCallback();
		}

		// Remove all connections and their elements
		this.connections.forEach((connection) => {
			connection.remove({ force: true, batchDelete: true });

			// For each connection, remove it also from the counterpart element that it is linked to
			const counterpartObject =
				connection.sourceParentObject === this
					? connection.targetParentObject
					: connection.sourceParentObject;

			const counterpartConnections = counterpartObject.connections;

			// Remove the connection from the counterpart object
			const updatedConnections = counterpartConnections.filter(
				(counterpartConnection) => connection !== counterpartConnection
			);

			counterpartObject.connections = updatedConnections;
		});

		this.connections = [];

		this.removeAllRelativeObjects();

		// Remove event listeners
		this.element.removeEventListener('mousedown', this.onElementMouseDown);
		document.removeEventListener('mouseup', this.onMouseUp);
		document.removeEventListener('mousemove', this.onDrag);

		this.element.removeEventListener('dblclick', this.onDoubleClickHandler);

		this._resizeObserver.disconnect();

		this.element?.remove();

		this.deleted = true;
	}

	handleClick() {
		// Select this object through the canvas instance.
		// Check if the shift keys are pressed. If so, add to selection, if not, unselect all other objects
		// Then select only this one
		if (this.canvas.isMultiselecting) {
			if (this.selected) {
				this.canvas.unselect(this);
				return;
			}

			this.canvas.select(this);
			return;

			// It is not multiselecting
		} else {
			this.canvas.unselectAll();

			if (this.selected) {
				this.canvas.unselect(this);
				return;
			}

			this.canvas.select(this);
			return;
		}
	}

	getElement() {
		return this.element;
	}

	resize(forced: boolean = false) {
		// Store the last known height of the element
		// Then get the new height and calculate the difference
		// Then add the difference to the y position
		const previousHeight = this.lastHeight;

		// If this is the first load, ignore
		if (previousHeight === 0) {
			return;
		}

		this.lastHeight = this.element.offsetHeight;

		const difference = this.lastHeight - previousHeight;
		const yChange = difference !== 0 ? difference / 2 : 0;

		// Add the difference to the y position
		// Otherwise if the element grows, it jumps up
		this.y = this.y + yChange;
		this.element.style.top = `${this.y}px`;

		this.updateConnections();
	}

	onMouseUp = (e: MouseEvent) => {
		// Ignore events flagged by our React components
		if ((e as any).__canvasIgnore) return;

		// It was only a click, not a drag
		if (!this.hasDraggedClick && this.isTargetOfMouseDown) {
			this.handleClick();
		} else if (this.isTargetOfMouseDown) {
			this.canvas.dragEnd();
		}

		this.isTargetOfMouseDown = false;
		this.hasDraggedClick = false;
		this.canvas.isMovingObjects = false;
		document.removeEventListener('mousemove', this.onDrag);
	};

	onDoubleClickHandler = (e: MouseEvent) => {
		// Ignore events flagged by our React components
		if ((e as any).__canvasIgnore) return;

		if (this.onDoubleClickCallback) {
			this.onDoubleClickCallback(e);
		}
	};

	onElementMouseDown = (e: MouseEvent) => {
		// Ignore events flagged by our React components
		if ((e as any).__canvasIgnore) return;

		// e.stopImmediatePropagation();
		// this.element.classList.add('active');

		this.isTargetOfMouseDown = true;
		document.addEventListener('mousemove', this.onDrag);
	};

	_boundResizeHandler = () => this.resize();

	setup() {
		this._resizeObserver.observe(this.element);

		this.element.id = this.id;
		// Add canvas id to the element
		this.element.setAttribute(Canvas.OBJ_ATTR_NAME, this.id);

		// Set up absolute positioning and starting coordinates
		this.element.style.position = 'absolute';
		this.element.style.left = `${this.x}px`;
		this.element.style.top = `${this.y}px`;

		// Add stope event propagation to the element so the canvas doesn't react to clicks on the cards
		// this.element.addEventListener('click', (e) => {
		// 	// e.stopImmediatePropagation();
		// });

		this.element.addEventListener('mousedown', this.onElementMouseDown);

		document.addEventListener('mouseup', this.onMouseUp);

		this.element.addEventListener('dblclick', this.onDoubleClickHandler);

		this.canvas.restoreScroll();
	}

	getCoordsPlusMovement({
		movementX,
		movementY
	}: {
		movementX: number;
		movementY: number;
	}): ICoordinates {
		const style = window.getComputedStyle(this.element);
		const leftVal = parseInt(style.left);
		const topVal = parseInt(style.top);

		const currentCanvasScale = this.canvas.currentScale;

		const scaledMovementX = movementX / currentCanvasScale;
		const scaledMovementY = movementY / currentCanvasScale;

		return {
			x: leftVal + scaledMovementX,
			y: topVal + scaledMovementY
		};
	}

	move = ({
		movementX,
		movementY
	}: {
		movementX: number;
		movementY: number;
	}) => {
		const newCoords = this.getCoordsPlusMovement({
			movementX,
			movementY
		});

		this.update({
			coordinates: newCoords,
			newConnections: [],
			deletedConnections: []
		});
	};

	onDrag = ({
		movementX,
		movementY
	}: {
		movementX: number;
		movementY: number;
	}) => {
		// This event is attached to the document so it gets triggered for all the objects.
		// We ignore the ones where dragging is false
		if (!this.isTargetOfMouseDown) {
			return;
		}

		// Don't drag elements, when the user its dragging its cursor over the canvas when drawing a new connection
		if (!!this.canvas.activeConnectionDrawing) {
			return;
		}

		// If this object is not selected when dragging, we need to select it
		// If the object is being dragged, and it isn't yet selected, we need to check if the user is currently mutiselecting
		// If the user is multiselecting, this is a new object that needs to be added to the selection
		// If not, we need to unselect all other objects and select only this one
		if (!this.selected) {
			if (this.canvas.isMultiselecting) {
				this.canvas.select(this);
			} else {
				this.canvas.unselectAll();
				this.canvas.select(this);
			}
		}

		// This means the draggging has just started
		if (!this.hasDraggedClick) {
			this.canvas.dragStart();
		}

		this.hasDraggedClick = true;
		this.canvas.isMovingObjects = true;

		// If the object is selected, through the canvas, move all other objects in the selection by the same amount
		// Filter itself and non draggable objects from the selection
		const objectsToMove = this.canvas.selectedObjects.filter(
			(object) => object instanceof DraggableObject
		) as DraggableObject[];

		// Through the canvas, we move all selected objects, which includes this one
		this.canvas.dragObjects(objectsToMove, {
			movementX,
			movementY
		});
	};

	// Triggered once at the beginning of the drag
	dragStart() {}

	// Triggered when the user releases the mouse button after dragging
	dragEnd() {
		this.onDragEndCallback?.({
			coordinates: {
				x: this.x,
				y: this.y
			},
			element: this.element,
			selected: this.selected,
			overlaps: [],
			newConnections: [],
			deletedConnections: []
		});
	}

	subscribeConnection(connection: Connection) {
		if (!this.connections.includes(connection)) {
			this.connections.push(connection);
		}
	}

	select() {
		if (!this.selectable) {
			return;
		}

		this.selected = true;

		// Select all connections of the object
		this.connections.forEach((connection) => {
			this.canvas.selectConnection(connection);
		});

		if (this.updateCallback) {
			this.updateCallback({
				element: this.element,
				coordinates: {
					x: this.x,
					y: this.y
				},
				selected: this.selected,
				newConnections: [],
				deletedConnections: [],
				// TODO: Should we calculate the overlaps? Or reuse a previous calculation?
				overlaps: []
			});
		}
	}

	unselect() {
		if (!this.selectable) {
			return;
		}

		this.selected = false;

		// Unselect all selected connections
		this.connections.forEach((connection) => {
			this.canvas.unselectConnection(connection);
		});

		if (this.updateCallback) {
			this.updateCallback({
				element: this.element,
				coordinates: {
					x: this.x,
					y: this.y
				},
				selected: this.selected,
				// TODO: Should we calculate the overlaps? Or reuse a previous calculation?
				overlaps: [],
				newConnections: [],
				deletedConnections: []
			});
		}
	}

	enable(options: { quiet: boolean } = { quiet: false }) {
		if (!this.disabled || this.canvas.disabled) {
			return;
		}

		this.disabled = false;

		if (this.onEnableCallback && !options.quiet) {
			this.onEnableCallback();
		}
	}

	disable(options: { quiet: boolean } = { quiet: false }) {
		this.disabled = true;

		if (this.onDisableCallback && !options.quiet) {
			this.onDisableCallback();
		}
	}

	overlapStart(overlappingWith: Overlaps) {
		if (this.onOverlapStartCallback) {
			this.onOverlapStartCallback(overlappingWith);
		}
	}

	overlapEnd(overlappingWith: Overlaps) {
		if (this.onOverlapEndCallback) {
			this.onOverlapEndCallback(overlappingWith);
		}
	}

	removeConnection(connection: Connection) {
		this.connections = this.connections.filter(
			(conn) => conn !== connection
		);
		connection.remove();

		const counterpartObject =
			connection.sourceParentObject === this
				? connection.targetParentObject
				: connection.sourceParentObject;
		const counterpartConnections = counterpartObject.connections;

		// Remove the connection from the counterpart object
		const updatedConnections = counterpartConnections.filter(
			(counterpartConnection) => connection !== counterpartConnection
		);

		counterpartObject.connections = updatedConnections;
	}

	removeAllConnections() {
		this.connections.forEach((connection) => {
			connection.remove();

			const counterpartObject =
				connection.sourceParentObject === this
					? connection.targetParentObject
					: connection.sourceParentObject;
			const counterpartConnections = counterpartObject.connections;

			// Remove the connection from the counterpart object
			const updatedConnections = counterpartConnections.filter(
				(counterpartConnection) => connection !== counterpartConnection
			);

			counterpartObject.connections = updatedConnections;
		});

		this.connections = [];
	}
}

/*
	This class manages the state of an ongoing connection drawing

	The users can click on a canvas element and drag while holding the mouse button
	to create a connection between two draggable objects, the starting object and either an existing object or a new one

	If the user releases the mouse button while dragging, the connection is created
	If the user release while on an existing object,
	the connection is created between the starting object and the existing one
	If the user releases on a free area of the canvas,
	a new object is created and the connection is created between the starting object and the new one
*/
export class ConnectionDrawing {
	sourceId: string;
	targetId: string;
	parentObject: DraggableObject | CanvasObject;

	canvas: Canvas;

	// d3 instances
	svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
	path: d3.Selection<SVGPathElement, unknown, HTMLElement, any>; // Hit Detection
	complementaryPath: d3.Selection<SVGPathElement, unknown, HTMLElement, any>; // Visible grey line
	marker: d3.Selection<SVGMarkerElement, unknown, HTMLElement, any>;
	markerId: string;

	// Group for custom SVG Chevrons
	chevronGroup: d3.Selection<SVGGElement, unknown, HTMLElement, any>;

	// Options
	options: IConnectionOptions;

	// The element the mouse is currently over
	overElement: HTMLElement | null = null;
	snappedTo: string | null = null;
	removed: boolean = false;
	cursorElement: HTMLElement | null = null;
	anchorElement: HTMLElement | null = null;

	id: string = Math.random().toString(36).substring(7);

	// Callbacks
	onEndCallback: (
		options: {
			coordinates: ICoordinates;
			element: HTMLElement | null;
		},
		connectionDrawing: ConnectionDrawing
	) => void;

	onCursorDrawingMoveCallback: (
		event: MouseEvent,
		options: {
			coordinates: ICoordinates;
			element: SVGElement | null;
			anchorElement: HTMLElement | null;
			cursorElement: HTMLElement | null;
		},
		connectionDrawing: ConnectionDrawing
	) => void;

	onMouseOverConnectableTargetCallback: (element: HTMLElement) => {
		snap?: boolean;
	};

	onMouseOutConnectableTargetCallback: (element: HTMLElement) => void;

	constructor(
		options: {
			sourceId?: string;
			targetId?: string;
			parentObject: DraggableObject | CanvasObject;
		},
		canvas: Canvas
	) {
		this.onMouseMove = this.onMouseMove.bind(this);
		this.onMouseUp = this.onMouseUp.bind(this);

		this.canvas = canvas;

		if (!options.sourceId && !options.targetId) {
			throw new Error(
				'Source and target ids are required to start a connection between canvas objects'
			);
		}

		if (!!options.sourceId && !!options.targetId) {
			throw new Error(
				'Only source or target ids can be provided, to start a connection between canvas objects'
			);
		}

		this.sourceId = options.sourceId;
		this.targetId = options.targetId;
		this.parentObject = options.parentObject;
	}

	get element(): SVGElement {
		return this.svg.node() as SVGElement;
	}

	get markerElement(): SVGMarkerElement {
		return this.marker.node() as SVGMarkerElement;
	}

	get lineElement(): SVGPathElement {
		return this.path.node() as SVGPathElement;
	}

	get complementaryLineElement(): SVGPathElement | null {
		return (this.complementaryPath?.node() as SVGPathElement) || null;
	}

	setupElements() {
		this.svg = d3
			.select('#' + this.canvas.id)
			.insert('svg', ':first-child')
			.attr('id', 'connection-svg--active-drawing--' + this.id)
			.style('position', 'absolute')
			.style('pointer-events', 'none');

		this.svg.node().classList.add('connector-line');

		this.generateMarker();

		// Support line (Visible Grey Line or Main Solid Line) goes on bottom
		this.complementaryPath = this.svg.append('path');

		// Invisible Hit Detection Path goes over it
		this.path = this.svg.append('path');

		// Group for our custom chevrons
		this.chevronGroup = this.svg
			.append('g')
			.style('pointer-events', 'none');
	}

	generateMarker(): d3.Selection<
		SVGMarkerElement,
		unknown,
		HTMLElement,
		any
	> {
		this.svg.select('defs').remove();

		// Feature flag for the active drawing line
		const isChevronStyle = !!this.options?.dash;

		// Matches the Connection class sizing
		const scale = isChevronStyle ? 2.5 : 1.2;

		const markerBoxWidth = 12 * scale;
		const markerBoxHeight = 12 * scale;
		const refX = markerBoxWidth - (isChevronStyle ? 2 : 1);
		const refY = markerBoxHeight / 2;
		const markerWidth = markerBoxWidth;
		const markerHeight = markerBoxHeight;

		const arrowPoints: Iterable<[number, number]> = [
			[0, 0],
			[12 * scale, 6 * scale],
			[0, 12 * scale],
			[3 * scale, 6 * scale]
		];

		this.marker = this.svg.append('svg:defs').append('svg:marker');
		const randomId = Math.random().toString(36).substring(7);
		this.markerId = `arrow-head--${this.id}--${randomId}`;

		this.marker
			.attr('id', this.markerId)
			.attr('viewBox', `0 0 ${markerBoxWidth} ${markerBoxHeight}`)
			.attr('refX', refX)
			.attr('refY', refY)
			.attr('markerUnits', 'userSpaceOnUse')
			.attr('markerWidth', markerWidth)
			.attr('markerHeight', markerHeight)
			.attr('orient', 'auto')
			.append('path')
			.attr('d', d3.line()(arrowPoints));

		return this.marker;
	}

	update(options?: { coordinates: ICoordinates }) {
		let source: HTMLElement = this.sourceId
			? this.anchorElement
			: this.cursorElement;

		let target: HTMLElement = this.targetId
			? this.anchorElement
			: this.cursorElement;

		let sourceCoords: {
			top: number;
			bottom: number;
			left: number;
			right: number;
		};

		let targetCoords: {
			top: number;
			bottom: number;
			left: number;
			right: number;
		};

		if (this.sourceId) {
			sourceCoords = getElementCanvasCoordinates(
				source,
				this.parentObject.element,
				this.canvas.currentScale
			);
		}

		if (this.targetId) {
			targetCoords = getElementCanvasCoordinates(
				target,
				this.parentObject.element,
				this.canvas.currentScale
			);
		}

		if (!this.snappedTo) {
			if (
				(!options?.coordinates.y && options?.coordinates.y !== 0) ||
				(!options?.coordinates.x && options?.coordinates.x !== 0)
			) {
				throw new Error(
					'Coordinates are required when the connection is not snapped'
				);
			}

			if (this.sourceId) {
				targetCoords = {
					top: options.coordinates.y,
					bottom: options.coordinates.y,
					left: options.coordinates.x,
					right: options.coordinates.x
				};
			}

			if (this.targetId) {
				sourceCoords = {
					top: options?.coordinates.y || 0,
					bottom: options?.coordinates.y || 0,
					left: options?.coordinates.x || 0,
					right: options?.coordinates.x || 0
				};
			}
		} else {
			const element = document.getElementById(this.snappedTo);

			if (!element) {
				throw new Error(
					`Element with id ${this.snappedTo} does not exist`
				);
			}

			const coords = getElementCanvasCoordinates(
				element,
				getCanvasParentElement(element, this.canvas.canvasElement),
				this.canvas.currentScale
			);

			if (this.sourceId) {
				targetCoords = coords;
				target = element;
			}

			if (this.targetId) {
				sourceCoords = coords;
				source = element;
			}
		}

		const sourceHeight = source.offsetHeight || source.clientHeight;
		const targetHeight = target.offsetHeight || target.clientHeight;
		const width = targetCoords.left - sourceCoords.right;

		let placement: 'right' | 'right-start' | 'right-end' | 'left' = 'right';

		if (sourceCoords.top < targetCoords.top) placement = 'right-start';
		if (sourceCoords.top > targetCoords.top) placement = 'right-end';
		if (
			sourceCoords.top < targetCoords.top &&
			sourceCoords.bottom > targetCoords.bottom
		)
			placement = 'right';
		if (
			sourceCoords.top > targetCoords.top &&
			sourceCoords.bottom < targetCoords.bottom
		)
			placement = 'left';

		const top = Math.min(
			sourceCoords.top,
			targetCoords.top,
			sourceCoords.bottom,
			targetCoords.bottom
		);
		const bottom = Math.max(
			sourceCoords.top,
			targetCoords.top,
			sourceCoords.bottom,
			targetCoords.bottom
		);

		const height = bottom - top;
		const absoluteElementsAreaHeight = bottom - top;

		const sourceCenterFromTop = sourceHeight / 2;
		const sourceCenterFromBottom = height - sourceHeight / 2;
		let linkSourceY: number = sourceCenterFromTop;

		const targetCenterFromTop = targetHeight / 2;
		const targetCenterFromBottom = height - targetHeight / 2;
		let linkTargetY: number = targetCenterFromTop;

		const markerHeight = 12;
		const calculatedSvgCanvasHeight =
			absoluteElementsAreaHeight + markerHeight * 2;

		if (sourceCoords.top < targetCoords.top) {
			linkSourceY = sourceCenterFromTop;
			linkTargetY = targetCenterFromBottom;
		}

		if (sourceCoords.top > targetCoords.top) {
			linkSourceY = sourceCenterFromBottom;
			linkTargetY = targetCenterFromTop;
		}

		if (
			sourceCoords.top < targetCoords.top &&
			sourceCoords.bottom > targetCoords.bottom
		) {
			linkSourceY = sourceCenterFromTop;
			const differenceTop = targetCoords.top - sourceCoords.top;
			linkTargetY = targetCenterFromTop + differenceTop;
		}

		if (
			sourceCoords.top > targetCoords.top &&
			sourceCoords.bottom < targetCoords.bottom
		) {
			const differenceTop = sourceCoords.top - targetCoords.top;
			linkSourceY = sourceCenterFromTop + differenceTop;
			linkTargetY = targetCenterFromTop;
		}

		linkTargetY += markerHeight;
		linkSourceY += markerHeight;

		const linkSource = { x: 0, y: linkSourceY };
		const linkTarget = { x: width, y: linkTargetY };

		this.svg.attr('width', width).attr('height', calculatedSvgCanvasHeight);

		const newCoords = positionRelativeTo(
			this.element as any as HTMLElement,
			placement.startsWith('left') ? target : source,
			this.parentObject.element,
			this.canvas.currentScale,
			placement
		);

		let resolvedY = newCoords.y;
		if (placement === 'right-start') resolvedY = newCoords.y - markerHeight;
		if (placement === 'right-end') resolvedY = newCoords.y + markerHeight;
		if (placement === 'left') resolvedY = newCoords.y;
		if (placement === 'right') resolvedY = newCoords.y;

		this.svg.style('top', resolvedY).style('left', newCoords.x);

		const link = d3
			.linkHorizontal<any, any, any>()
			.x((d: any) => d.x)
			.y((d: any) => d.y)({ source: linkSource, target: linkTarget });

		const isChevronStyle = !!this.options?.dash;
		const resolvedStateColor = this.options?.color || 'grey';
		const resolvedSize = this.options?.size || 3;

		this.marker
			.attr('stroke', resolvedStateColor)
			.attr('fill', resolvedStateColor)
			.select('path')
			.attr('stroke', resolvedStateColor)
			.attr('fill', resolvedStateColor);

		// 1. Render the main/support line
		this.complementaryPath
			.attr('d', link)
			.attr('stroke', isChevronStyle ? '#ccc' : resolvedStateColor)
			.attr('stroke-width', isChevronStyle ? 7 : resolvedSize)
			.attr('fill', 'none')
			.attr('marker-end', `url(#${this.markerId})`);

		// 2. Invisible Hit Detection
		this.path
			.attr('d', link)
			.attr('stroke-width', 20)
			.attr('stroke', 'transparent')
			.attr('fill', 'none')
			.style('pointer-events', 'none'); // During active drawing, we rarely need hit detection

		// 3. Render Chevrons IF feature flag is true
		if (isChevronStyle) {
			this.chevronGroup.style('display', null);

			const CHEVRON_SPACING = 16;
			const pathNode = this.complementaryPath.node() as SVGPathElement;
			const pathLength = pathNode.getTotalLength() || 0;

			const numChevrons = Math.floor(pathLength / CHEVRON_SPACING) + 2;
			const chevronsData = Array.from({ length: numChevrons }).map(
				(_, i) => (i - 1) * CHEVRON_SPACING
			);

			const chevrons = this.chevronGroup
				.selectAll('g.chevron')
				.data(chevronsData);

			const chevronsEnter = chevrons
				.enter()
				.append('g')
				.attr('class', 'chevron');

			chevronsEnter
				.append('path')
				.attr('d', 'M -6 -3.5 L 6 -3.5 L 8 0 L 6 3.5 L -6 3.5 L -4 0 Z')
				.attr('stroke', 'none');

			chevrons.exit().remove();

			const allChevrons = chevronsEnter.merge(chevrons as any);
			allChevrons.select('path').attr('fill', resolvedStateColor);

			allChevrons.each(function (d: number) {
				d3.select(this).attr('data-base-length', d.toString());
			});

			// Render static chevrons at 0 offset because the path is actively changing length
			this.positionChevrons(0);
		} else {
			this.chevronGroup.style('display', 'none');
		}
	}

	// Same alignment helper as the Connection class
	positionChevrons(offset: number) {
		const pathNode = this.complementaryPath.node() as SVGPathElement;
		const pathLength = pathNode.getTotalLength() || 0;

		this.chevronGroup.selectAll('g.chevron').attr('transform', function () {
			const baseLen = parseFloat(
				d3.select(this).attr('data-base-length')
			);
			let targetLen = baseLen + offset;

			if (targetLen < 0 || targetLen > pathLength) {
				d3.select(this).attr('opacity', 0);
				return `translate(-100,-100)`;
			} else {
				d3.select(this).attr('opacity', 1);
			}

			const pt = pathNode.getPointAtLength(targetLen);
			const pt2 = pathNode.getPointAtLength(
				Math.min(targetLen + 1, pathLength)
			);
			let angle =
				Math.atan2(pt2.y - pt.y, pt2.x - pt.x) * (180 / Math.PI);

			return `translate(${pt.x}, ${pt.y}) rotate(${angle})`;
		});
	}

	onEnd(
		callback: (
			options: {
				coordinates: ICoordinates;
				element: HTMLElement | null;
			},
			connectionDrawing: ConnectionDrawing
		) => void
	): ConnectionDrawing {
		this.onEndCallback = callback;
		return this;
	}

	onCursorDrawingMove(
		callback: (
			e: MouseEvent,
			options: {
				coordinates: ICoordinates;
				element: SVGElement | null;
				anchorElement: HTMLElement | null;
				cursorElement: HTMLElement | null;
			},
			connectionDrawing: ConnectionDrawing
		) => void
	): ConnectionDrawing {
		this.onCursorDrawingMoveCallback = callback;
		return this;
	}

	onMouseOverConnectableTarget(
		callback: (element: HTMLElement) => { snap?: boolean }
	): ConnectionDrawing {
		this.onMouseOverConnectableTargetCallback = callback;
		return this;
	}

	onMouseOutConnectableTarget(
		callback: (element: HTMLElement) => void
	): ConnectionDrawing {
		this.onMouseOutConnectableTargetCallback = callback;
		return this;
	}

	onMouseMove(e: MouseEvent) {
		if (this.removed) return;
		if (this.snappedTo) return;

		const canvasRect = this.canvas.canvasElement.getBoundingClientRect();

		const mouseX =
			e.clientX / this.canvas.currentScale -
			canvasRect.left / this.canvas.currentScale;
		const mouseY =
			e.clientY / this.canvas.currentScale -
			canvasRect.top / this.canvas.currentScale;

		this.cursorElement.style.left = mouseX + 'px';
		this.cursorElement.style.top = mouseY + 'px';

		this.update({ coordinates: { x: mouseX, y: mouseY } });
		this.onCursorDrawingMoveCallback?.(
			e,
			{
				coordinates: { x: mouseX, y: mouseY },
				element: this.element,
				anchorElement: this.anchorElement,
				cursorElement: this.cursorElement
			},
			this
		);
	}

	snap(elementId: string) {
		const element = document.getElementById(elementId);
		if (!element) {
			Logger.warn(
				`Element with id '${elementId}' not found, cannot snap to it`
			);
			return;
		}
		this.snappedTo = elementId;
		this.update();
	}

	unSnap() {
		this.snappedTo = null;
	}

	private onMouseUp(event: MouseEvent) {
		if (this.removed) return;

		document.removeEventListener('mousemove', this.onMouseMove);
		document.body.style.userSelect = 'auto';

		this.canvas.canvasElement.classList.remove('drawing-connector');
		this.canvas.persistScroll();

		const canvasRect = this.canvas.canvasElement.getBoundingClientRect();

		const mouseX =
			event.clientX / this.canvas.currentScale -
			canvasRect.left / this.canvas.currentScale;
		const mouseY =
			event.clientY / this.canvas.currentScale -
			canvasRect.top / this.canvas.currentScale;

		this.onEndCallback(
			{
				coordinates: { x: mouseX, y: mouseY },
				element: this.overElement || null
			},
			this
		);

		this.canvas.ignoreNextClick = true;
		this.canvas.restoreScroll();

		document.removeEventListener('mousemove', this.onMouseMove);
		document.removeEventListener('mouseup', this.onMouseUp);

		return this;
	}

	setOptions(options: IConnectionOptions): ConnectionDrawing {
		this.options = options;
		return this;
	}

	init(): ConnectionDrawing {
		this.canvas.activeConnectionDrawing = this;
		this.canvas.lastConnectionDrawing = this.canvas.activeConnectionDrawing;

		this.canvas.startAutoPanLoop();
		this.canvas.unselectAll();

		document.body.style.userSelect = 'none';

		this.anchorElement = document.getElementById(
			this.sourceId || this.targetId
		);

		if (!this.anchorElement) {
			throw new Error(
				`Anchor element with id '${this.sourceId || this.targetId}' not found, cannot start connection drawing`
			);
		}

		this.cursorElement = document.createElement('div');
		this.cursorElement.style.position = 'absolute';
		this.cursorElement.style.width = '1px';
		this.cursorElement.style.height = '1px';
		this.cursorElement.style.background = 'transparent';
		this.cursorElement.style.zIndex = '100';
		this.cursorElement.style.pointerEvents = 'none';
		this.cursorElement.style.top = '0';
		this.cursorElement.style.left = '0';

		this.canvas.canvasElement.appendChild(this.cursorElement);

		document.addEventListener('mousemove', this.onMouseMove);
		document.addEventListener('mouseup', this.onMouseUp);

		this.canvas.canvasElement.classList.add('drawing-connector');

		this.setupElements();

		return this;
	}

	removeLine(): ConnectionDrawing {
		this.svg.remove();
		return this;
	}

	remove(): ConnectionDrawing {
		this.removed = true;
		document.removeEventListener('mousemove', this.onMouseMove);
		document.removeEventListener('mouseup', this.onMouseUp);
		this.removeLine();
		this.cursorElement?.remove();
		return this;
	}
}

/*
	This class is the instance of one canvas in the UI. There could be multiple instances/canvas in one application.
	The purpose is to hold all the data that control the lower level functionality and UX of the canvas as an engine of canvas interaction,
	which is agnostic to the use-cases that implement it.
	This class should therefore implement all logic without referencing external behavior that might or might not be implemented by the user:
	It doesn't know what the entities look like, it doesn't know how the canvas UX looks like,
	it only provides an abstraction to achieve with HTML what is usually achieves with a 'canvas' tag,
	but with the added benefit of being HTML and JavaScript that the browser can interact with.
*/
export class Canvas extends UndoableEntity {
	ready: boolean = false;
	anchorElement: HTMLElement;
	anchorElementQuerySelector: string;
	canvasElement: HTMLElement;
	elementClasses: string[] = [];

	draggableObjects: DraggableObject[] = [];
	nonDraggableObjects: CanvasObject[] = [];
	// A copy of the key properties of each object as they were when they were created. This is used to initialize the undoable entity
	initialValues: IDraggableUndoableValue[] = [];
	id: string;
	enableUndoRedo: boolean = true;
	invertScrollDirection: boolean = false;

	// Zoom
	currentScale = 1;
	hasOnceInSessionZoomed = false;

	disabled: boolean = false;

	// Moving objects
	// Wether the canvas is currently dragging objects
	isMovingObjects: boolean = false;

	// Wether the canvas is currently moving/panning/scrolling
	isMovingCanvas: boolean = false;
	private wheelEndTimeout: number | null = null;

	// Edge panning
	lastGlobalMouseMoveEvent: MouseEvent | null = null;
	autoPanFrameId: number | null = null;
	autoPanEdgeThreshold: number = 50;
	autoPanSpeed: number = 15;

	// Canvas movement/panning/scrolling
	hasMovedDragged = false;
	hasOnceInSessionMovedInCanvas = false;
	originalMouseDownX: null | number = null;
	originalMouseDownY: null | number = null;
	originalScrollXLocation: null | number = null;
	originalScrollYLocation: null | number = null;
	naiveUnscaledDocumentScrollX!: number;
	naiveUnscaledDocumentScrollY!: number;

	// New connection being drawn
	activeConnectionDrawing: ConnectionDrawing | null = null;
	lastConnectionDrawing: ConnectionDrawing | null = null;

	// Area selection
	selectionEnabled: boolean = true;
	selectionBox: HTMLElement | null = null;
	selectionBoxLeft: number = 0;
	selectionBoxTop: number = 0;
	selectionBoxRight: number = 0;
	selectionBoxBottom: number = 0;
	isSelecting = false;
	hasOnceInSessionSelected = false;
	selectedObjects: (CanvasObject | DraggableObject)[] = [];
	selectedConnections: Connection[] = [];
	// This is a temporary selection to keep track of which objects were selected in the current selection,
	// This is useful when the user holds shift and selects in multiple actions/selection areas
	selectedInCurrentSelectionAction: (CanvasObject | DraggableObject)[] = [];

	// Click
	hasDraggedClick: boolean = false;
	hasOnceInSessionClickedCanvas: boolean = false;
	ignoreNextClick: boolean = false;

	// Tooltip
	tooltip: HTMLElement | null = null;
	userSetTooltipText: string | null = null;
	activeUserSetTooltipId: string | null = null;
	cancelShowTooltip: boolean = false;
	tooltipTimeout: null | ReturnType<typeof setTimeout> = null;
	hasShownClickTooltip = false;
	hasShownPanTooltip = false;
	hasShownDragElementTooltip = false;
	hasShownZoomTooltip = false;
	hasShownSelectTooltip = false;

	// Key shortcuts
	keysCurrentlyPressed: string[] = [];

	// The canvas size should be bigger than the space taken by the elements
	// So we keep track of the highest and lowest point of the furthest elements
	contentTop: number = 0;
	contentBottom: number = 0;
	contentLeft: number = 0;
	contentRight: number = 0;

	// Callbacks
	onClickCallback:
		| null
		| ((coordinate: { x: number; y: number }, event: MouseEvent) => void) =
		null;
	onRightClickCallback:
		| null
		| ((coordinate: { x: number; y: number }, event: MouseEvent) => void) =
		null;
	onDoubleClickCallback:
		| null
		| ((coordinate: { x: number; y: number }, event: MouseEvent) => void) =
		null;
	onPanCallback: null | ((event: MouseEvent) => void) = null;
	onSelectingCallback: null | ((event: MouseEvent) => void) = null;
	onSelectEndCallback: null | ((event: MouseEvent) => void) = null;
	onCopyCallback:
		| null
		| ((event: ClipboardEvent, objects: IDraggableValue[]) => void) = null;
	onPasteCallback: null | ((clipboardData: any) => void) = null;
	onExitCallback: null | (() => void) = null;
	onShouldResolveOwnInitialScrollCallback: null | (() => void) = null;
	onBeforeScrollToThisCallback:
		| null
		| ((objectId: string) => Promise<void> | void) = null;
	onDragEndCallback:
		| null
		| ((objects: (CanvasObject | DraggableObject)[]) => void) = null;
	onDeleteCallback:
		| null
		| ((objects: (CanvasObject | DraggableObject)[]) => void) = null;
	onUndoCallback: null | (() => void) = null;
	onRedoCallback: null | (() => void) = null;
	onScaleChangeCallback: null | ((scale: number) => void) = null;

	get maxScale() {
		return 1.5;
	}

	get minScale() {
		return 0.1;
	}

	// Getters
	get hasShownTooltip(): boolean {
		return (
			this.hasShownClickTooltip ||
			this.hasShownDragElementTooltip ||
			this.hasShownPanTooltip
		);
	}

	get isTooltipOpen() {
		return (
			!!this.tooltip &&
			this.tooltip.style.display !== 'none' &&
			!this.tooltip.hidden &&
			this.canvasElement.contains(this.tooltip)
		);
	}

	get isMultiselecting(): boolean {
		return !!(
			this.keysCurrentlyPressed.includes('ShiftLeft') ||
			this.keysCurrentlyPressed.includes('ShiftRight')
		);
	}

	get objects(): (DraggableObject | CanvasObject)[] {
		return [...this.draggableObjects, ...this.nonDraggableObjects];
	}

	get isUndoableStackUninitialized(): boolean {
		return this.undoableStackValueIndex === 0;
	}

	static OBJ_ATTR_NAME = 'canvas-object-id';

	static selectByAttribute(attributeValue: string) {
		return window.document.querySelector(
			`[${Canvas.OBJ_ATTR_NAME}="${attributeValue}"]`
		);
	}

	static getCurrentScrollX() {
		const scrollX =
			document.documentElement.scrollLeft || document.body.scrollLeft;

		return scrollX;
	}

	static getCurrentScrollY() {
		const scrollY =
			document.documentElement.scrollTop || document.body.scrollTop;

		return scrollY;
	}

	// Get the scale from local storage or DOM element
	static getCanvasScale(canvasId: string) {
		const canvasScale = localStorage.getItem(`canvas--${canvasId}--scale`);

		if (canvasScale) {
			return parseFloat(canvasScale);
		}

		// If there is no scale in local storage, get it from the DOM element
		const canvasElement = document.getElementById(canvasId);

		if (canvasElement) {
			// @ts-ignore
			return parseFloat(canvasElement.style.transform || '1');
		}

		return 1;
	}

	constructor({
		containerElement,
		id,
		connectionDefaults,
		elementClasses,
		enableUndoRedo,
		invertScrollDirection
	}: ICanvasOptions) {
		// Initialize the undoable entity with null as the initial value,
		// this helps us know that no changes came before as all other values will be an array of canvas elements
		super([]);

		Connection.setDefaults(connectionDefaults || {});

		this.anchorElement = containerElement;
		this.elementClasses = elementClasses || [];
		this.id = id;
		this.enableUndoRedo = enableUndoRedo === false ? false : true;
		this.invertScrollDirection = invertScrollDirection === true;

		// Bindings
		this.startMoveCapture = this.startMoveCapture.bind(this);
		this.draggingOnCanvas = this.draggingOnCanvas.bind(this);
		this.endMoveCapture = this.endMoveCapture.bind(this);
		this.onGeneralKeyDown = this.onGeneralKeyDown.bind(this);
		this.onGeneralKeyUp = this.onGeneralKeyUp.bind(this);
		this.onWindowBlur = this.onWindowBlur.bind(this);
		this.onCanvasMovementKeyDown = this.onCanvasMovementKeyDown.bind(this);
		this.onCanvasMovementKeyUp = this.onCanvasMovementKeyUp.bind(this);
		this.onCanvasMovementMouseDown =
			this.onCanvasMovementMouseDown.bind(this);
		this.onCanvasMovementMouseUp = this.onCanvasMovementMouseUp.bind(this);
		this.handleOnSelectionMouseDown =
			this.handleOnSelectionMouseDown.bind(this);
		this.endSelection = this.endSelection.bind(this);
		this.handleOnSelectionMouseMove =
			this.handleOnSelectionMouseMove.bind(this);
		this.handleOnCanvasClick = this.handleOnCanvasClick.bind(this);
		this.handleOnCanvasRightClick =
			this.handleOnCanvasRightClick.bind(this);
		this.onClickAndDrag = this.onClickAndDrag.bind(this);
		this.handleOnClickMouseDown = this.handleOnClickMouseDown.bind(this);
		this.handleOnClickMouseUp = this.handleOnClickMouseUp.bind(this);
		this.handleOnCanvasDoubleClick =
			this.handleOnCanvasDoubleClick.bind(this);
		this.handleOnCursorTooltipMouseLeave =
			this.handleOnCursorTooltipMouseLeave.bind(this);
		this.handleOnCursorTooltipMouseMove =
			this.handleOnCursorTooltipMouseMove.bind(this);
		this.handleOnZoomMouseWheel = this.handleOnZoomMouseWheel.bind(this);
		this.handleOnGlobalMouseMove = this.handleOnGlobalMouseMove.bind(this);
		this.startAutoPanLoop = this.startAutoPanLoop.bind(this);
		this.doAutoPan = this.doAutoPan.bind(this);
		this.stopAutoPanLoop = this.stopAutoPanLoop.bind(this);
		this.handleResize = this.handleResize.bind(this);
		this.handleCopy = this.handleCopy.bind(this);
		this.handlePaste = this.handlePaste.bind(this);

		// Initialize
		this.setup();
	}

	disableSelection() {
		this.selectionEnabled = false;

		// If the selection box is visible, hide it
		this.clearSelection();
	}

	enableSelection() {
		this.selectionEnabled = true;
	}

	createTestObjectAtTopLeft() {
		const element = document.createElement('div');
		// Red background 100x100px
		element.style.width = '100px';
		element.style.height = '100px';
		element.style.backgroundColor = 'red';

		// Apend to canvas
		this.canvasElement.appendChild(element);

		new DraggableObject({
			coordinates: { x: 0, y: 0 },
			id: 'test-object',
			element: element,
			canvas: this
		});

		const scrollXBefore = Canvas.getCurrentScrollX() / this.currentScale;
		const scrollYBefore = Canvas.getCurrentScrollY() / this.currentScale;

		this.naiveUnscaledDocumentScrollX = scrollXBefore;
		this.naiveUnscaledDocumentScrollY = scrollYBefore;
		const scaledScrollXBefore = Canvas.scaleScrollValue(
			this.naiveUnscaledDocumentScrollX,
			this.currentScale
		);
		const scaledScrollYBefore = Canvas.scaleScrollValue(
			this.naiveUnscaledDocumentScrollY,
			this.currentScale
		);

		// scroll to 0,0
		window.scroll(11000, 14000);

		const scrollXAfter = Canvas.getCurrentScrollX() / this.currentScale;
		const scrollYAfter = Canvas.getCurrentScrollY() / this.currentScale;

		this.naiveUnscaledDocumentScrollX = scrollXAfter;
		this.naiveUnscaledDocumentScrollY = scrollYAfter;
		const scaledScrollXAfter = Canvas.scaleScrollValue(
			this.naiveUnscaledDocumentScrollX,
			this.currentScale
		);
		const scaledScrollYAfter = Canvas.scaleScrollValue(
			this.naiveUnscaledDocumentScrollY,
			this.currentScale
		);
	}

	// Refreshes the DOM by re-adding all the objects to the canvas
	refresh() {
		this.setupElement();
	}

	getDraggableById(id: string): DraggableObject | undefined {
		return this.draggableObjects.find((object) => object.id === id);
	}

	// This method tries to restore values to the canvas state, given 2 arrays of objects that represent the state of canvas objects
	// The first array is the restored value, meaning the values that are not curretnly active, but should be active after the reconciliation
	// The second array are the current values, meaning the values that are currently active and should be replaced by the restored values after the reconciliation
	// For examples on the usage check the 'onUndo' and 'onRedo' methods
	reconcileRestoredState(
		restoredValues: IDraggableValue[],
		valuesBeingReplace: IDraggableValue[]
	): any {
		this.persistScroll();

		// Reconcile old state with new incoming state.
		// We have to account for 3 cases:
		// 1. An object was added in the restored value, so it isn't present or or is marked as deleted in the previous value
		// 2. An object was removed or marked as deleted in the restored value, so it is in the previous value
		// 3. An object is in both the restored value and the previous value, then it was just updated

		restoredValues.forEach((restoredObject) => {
			const restoredObjectInstance = restoredObject.instance;

			if (restoredObject.deleted) {
				// If the object is marked as deleted in the restored value, but it is present in the previous value, delete it
				if (restoredObjectInstance) {
					restoredObjectInstance.remove();
				}

				return;
			}

			// If it is marked as deleted on the previous value, but not on the restored value, re-add it
			const objectToBeReplaced = valuesBeingReplace.find(
				(objToBeReplaced) => {
					return objToBeReplaced.id === restoredObject.id;
				}
			);

			const connectionsToBeReplaced: IConnectionValue[] = [];

			if (objectToBeReplaced) {
				// If the previous object exists and it is not marked as deleted,
				// use it to compare the connections and update the new object
				connectionsToBeReplaced.push(...objectToBeReplaced.connections);

				if (objectToBeReplaced.deleted) {
					if (restoredObjectInstance) {
						// Calculate changes in connections
						// Fully restored connections that were deleted before or have been created new
						const newConnections: IConnectionValue[] =
							connectionsToBeReplaced.filter(
								// If it is marked as deleted on the previous value, but not on the restored value, re-add it
								(conn) => {
									if (conn.deleted) {
										const resoredConn =
											restoredObject.connections.find(
												(restoredConn) =>
													restoredConn.id === conn.id
											);

										return (
											resoredConn && !resoredConn.deleted
										);
									}

									return false;
								}
							);

						// Fully deleted connections that were present before but are not present anymore
						const deletedConnections: IConnectionValue[] =
							restoredObject.connections.filter((conn) => {
								if (conn.deleted) {
									const previousConn =
										connectionsToBeReplaced.find(
											(previousConn) =>
												previousConn.id === conn.id
										);

									return (
										previousConn && !previousConn.deleted
									);
								}
							});

						restoredObjectInstance.restore({
							coordinates: {
								x: restoredObject.x,
								y: restoredObject.y
							},
							element: restoredObjectInstance.element,
							newConnections: newConnections,
							deletedConnections: deletedConnections
						});

						if (restoredObjectInstance instanceof DraggableObject) {
							this.draggableObjects.push(restoredObjectInstance);
						}
					}
					return;
				}
			}

			// Calculate changes in connections
			// Fully restored connections that were deleted before or have been created new
			const newConnections: IConnectionValue[] =
				connectionsToBeReplaced.filter(
					// If it is marked as deleted on the previous value, but not on the restored value, re-add it
					(conn) => {
						if (conn.deleted) {
							const resoredConn = restoredObject.connections.find(
								(restoredConn) => restoredConn.id === conn.id
							);

							return resoredConn && !resoredConn.deleted;
						}

						return false;
					}
				);

			// Fully deleted connections that were present before but are not present anymore
			const deletedConnections: IConnectionValue[] =
				restoredObject.connections.filter((conn) => {
					if (conn.deleted) {
						const previousConn = connectionsToBeReplaced.find(
							(previousConn) => previousConn.id === conn.id
						);

						return previousConn && !previousConn.deleted;
					}
				});

			// If it is neither marked as deleted on the previous value or the restored value, update it
			restoredObjectInstance.update(
				{
					coordinates: {
						x: restoredObject.x,
						y: restoredObject.y
					},
					element: restoredObjectInstance.element,
					newConnections,
					deletedConnections

					// Depending on the order in which this loop updates objects,
					// they might overlap whith eachother,
					// we avoid any side effects by cancelling the checks
				},
				{ skipOverlapCheck: true }
			);
		});
	}

	// Abstract undoable entity methods implementation - Undo/redo method
	onUndo(
		// Here the 'restoredValue' is the oldest value, as we are going back in our changes stack
		restoredValue: IDraggableValue[],
		// This is the value that was active until now
		previousValue: IDraggableValue[]
	): any {
		Logger.log('Undoing canvas changes');
		this.reconcileRestoredState(restoredValue, previousValue);
	}

	onRedo(
		// Here the 'restoredValue' is the newest value, as we are going forward in our changes stack
		restoredValue: IDraggableValue[],
		// This is the value that was active until now, in this case an older value that was restored previously
		previousValue: IDraggableValue[]
	): any {
		Logger.log('Redoing canvas changes');
		this.reconcileRestoredState(restoredValue, previousValue);
	}

	onClick(
		callback: (
			coordinate: { x: number; y: number },
			event: MouseEvent
		) => void
	) {
		this.onClickCallback = callback;
	}

	offClick() {
		this.onClickCallback = null;
	}

	onRightClick(
		callback: (
			coordinate: { x: number; y: number },
			event: MouseEvent
		) => void
	) {
		this.onRightClickCallback = callback;
	}

	offRightClick() {
		this.onRightClickCallback = null;
	}

	onDoubleClick(
		callback: (
			coordinate: { x: number; y: number },
			event: MouseEvent
		) => void
	) {
		this.onDoubleClickCallback = callback;
	}

	offDoubleClick() {
		this.onDoubleClickCallback = null;
	}

	// For backspace and delete keys
	onExit(callback: () => void) {
		this.onExitCallback = callback;
	}

	offExit() {
		this.onExitCallback = null;
	}

	onPan(callback: (event: MouseEvent) => void) {
		this.onPanCallback = callback;
	}

	offPan() {
		this.onPanCallback = null;
	}

	onSelecting(callback: (event: MouseEvent) => void) {
		this.onSelectingCallback = callback;
	}

	offSelecting() {
		this.onSelectingCallback = null;
	}

	onSelectEnd(callback: (event: MouseEvent) => void) {
		this.onSelectEndCallback = callback;
	}

	offSelectEnd() {
		this.onSelectEndCallback = null;
	}

	onCopy(
		callback: (event: ClipboardEvent, objects: IDraggableValue[]) => void
	) {
		this.onCopyCallback = callback;
	}

	offCopy() {
		this.onCopyCallback = null;
	}

	onPaste(callback: (clipboardData: any) => void) {
		this.onPasteCallback = callback;
	}

	offPaste() {
		this.onPasteCallback = null;
	}

	onShouldResolveOwnInitialScroll(callback: () => void) {
		this.onShouldResolveOwnInitialScrollCallback = callback;
	}

	offShouldResolveOwnInitialScroll() {
		this.onShouldResolveOwnInitialScrollCallback = null;
	}

	onScaleChange(callback: (scale: number) => void): Canvas {
		this.onScaleChangeCallback = callback;

		return this;
	}

	offScaleChange() {
		this.onScaleChangeCallback = null;
	}

	// Add key to list of keys currently pressed if it isn't already there
	addKeyPressTracking(code: string) {
		if (!this.keysCurrentlyPressed.includes(code)) {
			this.keysCurrentlyPressed.push(code);
		}
	}

	// Remove key from list of keys currently pressed
	removeKeyPressTracking(code: string) {
		this.keysCurrentlyPressed = this.keysCurrentlyPressed.filter(
			(key) => key !== code
		);
	}

	// Get width and height of canvas and scroll to its center
	center() {
		const canvasWidth = this.canvasElement.offsetWidth;
		const canvasHeight = this.canvasElement.offsetHeight;
		const canvasCenterX = canvasWidth / 2;
		const canvasCenterY = canvasHeight / 2;

		const unscaledCanvasCenterX = canvasCenterX * this.currentScale;
		const unscaledCanvasCenterY = canvasCenterY * this.currentScale;
		this.scrollTo(unscaledCanvasCenterX, unscaledCanvasCenterY);
	}

	setupUndoRedo() {
		// We apend the detector element to the body instead of the canvas,
		// so we don't need to worry about the canvas being moved or unmounted
		// Because the state of this element needs to be preserved by the browser
		if (this.enableUndoRedo) {
			addUndoEventListener(this, this.id, document.body);
		}
	}

	handleCopy(event: ClipboardEvent) {
		// Get the selected text
		const selection = window.getSelection();
		const selectedText = selection.toString();

		// Default copy behaviour
		if (selectedText) {
			return;
		} else if (this.selectedObjects.length > 0) {
			const selectedObjectsValues: IDraggableValue[] =
				this.selectedObjects.map((obj) => ({
					...this.toUndoableValue(obj as DraggableObject, {
						noInstance: true
					}),
					element: obj.element
				}));

			if (selectedObjectsValues.length === 0) {
				return;
			}

			this.onCopyCallback?.(event, selectedObjectsValues);
		}
	}

	handlePaste(event: ClipboardEvent) {
		// Get the clipboard contents as plain text
		// @ts-ignore
		const clipboardData = event.clipboardData || window.clipboardData;

		if (!!clipboardData) {
			// Try to parse data as JSON to convert to an object
			try {
				this.onPasteCallback?.(clipboardData);
			} catch (error) {
				// If the data is not JSON, we can't do anything with it
				return;
			}
		}
	}

	onCanvasMovementKeyDown(e: KeyboardEvent) {
		this.addKeyPressTracking(e.code);

		if (e.code == 'Space' && e.target == document.body) {
			e.preventDefault();

			this.canvasElement.style.cursor = 'grab';
			this.canvasElement.addEventListener(
				'mousedown',
				this.startMoveCapture
			);
			document.addEventListener('mouseup', this.endMoveCapture);
		}
	}

	onCanvasMovementKeyUp(e: KeyboardEvent) {
		this.removeKeyPressTracking(e.code);

		if (e.code == 'Space' && e.target == document.body) {
			e.preventDefault();

			this.canvasElement.style.cursor = 'auto';
			this.canvasElement.removeEventListener(
				'mousedown',
				this.startMoveCapture
			);
			document.removeEventListener('mouseup', this.endMoveCapture);
		}
	}

	onCanvasMovementMouseDown(e: MouseEvent) {
		if (e.button === 1) {
			this.hasMovedDragged = true;
			this.canvasElement.style.cursor = 'grab';
			this.startMoveCapture(e);
		}
	}

	onCanvasMovementMouseUp(e: MouseEvent) {
		if (e.button === 1) {
			this.canvasElement.style.cursor = 'auto';
			this.endMoveCapture(e);
		}
	}

	setupCanvasMovement() {
		window.document.body.style.overflow = 'hidden';

		window.addEventListener('keydown', this.onCanvasMovementKeyDown);

		window.addEventListener('keyup', this.onCanvasMovementKeyUp);

		// Use the auxiliary mouse button to pan the canvas
		window.addEventListener('mousedown', this.onCanvasMovementMouseDown);

		window.addEventListener('mouseup', this.onCanvasMovementMouseUp);
	}

	// This method tears down the event listeners and other state set up by the 'setupCanvasMovement' method
	// Including the events set up by event listeners themselfes if any, and if they don't handle their own cleanup
	teardownCanvasMovement() {
		window.document.body.style.overflow = 'auto';

		window.removeEventListener('keydown', this.onCanvasMovementKeyDown);

		window.removeEventListener('keyup', this.onCanvasMovementKeyUp);

		window.removeEventListener('mousedown', this.onCanvasMovementMouseDown);

		window.removeEventListener('mouseup', this.onCanvasMovementMouseUp);
	}

	unselectAll() {
		this.selectedObjects.forEach((object) => {
			object.unselect();
		});

		this.unselectAllConnections();

		this.selectedObjects = [];
		this.selectedInCurrentSelectionAction = [];
	}

	unselectAllConnections() {
		this.selectedConnections.forEach((connection) => {
			connection.unselect();
		});

		this.selectedConnections = [];
	}

	async unselectAllAsync(options: YieldOptions = {}) {
		const tracker = YieldTracker.from(options);

		for (const obj of this.selectedObjects) {
			obj.unselect();

			await tracker.tick();
		}

		await this.unselectAllConnectionsAsync(options);

		this.selectedObjects = [];
		this.selectedInCurrentSelectionAction = [];
	}

	async unselectAllConnectionsAsync(options: YieldOptions = {}) {
		const tracker = YieldTracker.from(options);

		for (const connection of this.selectedConnections) {
			connection.unselect();

			await tracker.tick();
		}

		this.selectedConnections = [];
	}

	select(canvasObject: CanvasObject | DraggableObject) {
		if (canvasObject.disabled || this.disabled) {
			return;
		}

		if (!canvasObject.selectable) {
			return;
		}

		if (!canvasObject.selected) {
			canvasObject.select();
		}

		if (!this.selectedObjects.includes(canvasObject)) {
			this.selectedObjects.push(canvasObject);
		}
	}

	selectConnection(connection: Connection) {
		if (!connection.selected) {
			connection.select();
		}

		if (!this.selectedConnections.includes(connection)) {
			this.selectedConnections.push(connection);
		}
	}

	unselectConnection(connection: Connection) {
		if (connection.selected) {
			connection.unselect();
		}

		this.selectedConnections = this.selectedConnections.filter(
			(selectedConnection) => selectedConnection !== connection
		);
	}

	unselect(canvasObject: CanvasObject | DraggableObject) {
		if (canvasObject.selected) {
			canvasObject.unselect();
		}

		this.selectedObjects = this.selectedObjects.filter(
			(object) => object !== canvasObject
		);
	}

	enable(
		canvasObject: DraggableObject,
		options: { quiet: boolean } = { quiet: false }
	) {
		if (canvasObject.disabled && !this.disabled) {
			canvasObject.enable(options);
		}
	}

	// Disabling an entity makes it not responsive to events and interactions
	disable(
		canvasObject: DraggableObject,
		options: { quiet: boolean } = { quiet: false }
	) {
		if (!canvasObject.disabled) {
			canvasObject.disable(options);
		}
	}

	enableAllConnections() {
		this.objects.forEach((object) => {
			if (object instanceof DraggableObject) {
				object.connections.forEach((connection) => {
					connection.enable();
				});
			} else if (object instanceof CanvasObject) {
				object.connections.forEach((connection) => {
					connection.enable();
				});
			}
		});
	}

	disableAllConnections() {
		this.objects.forEach((object) => {
			if (object instanceof DraggableObject) {
				object.connections.forEach((connection) => {
					connection.disable();
				});
			} else if (object instanceof CanvasObject) {
				object.connections.forEach((connection) => {
					connection.disable();
				});
			}
		});
	}

	handleOnClickMouseDown(e: MouseEvent) {
		this.canvasElement.addEventListener('mousemove', this.onClickAndDrag);
	}

	onClickAndDrag(event: MouseEvent) {
		// Log how much it has dragged
		const movementX = event.movementX;
		const movementY = event.movementY;

		// In the future we could fine-tune what we consider to be a drag or a click by checking how much the cursor moved
		// This could have UX benefits because currently, it is very easy to accidentally drag while clicking for at least a few pixels
		if (movementX || movementY) {
			// Logger.log('movementX', movementX);
			// Logger.log('movementY', movementY);
		}

		this.hasDraggedClick = true;
	}

	handleOnClickMouseUp(e: MouseEvent) {
		this.canvasElement.removeEventListener(
			'mousemove',
			this.onClickAndDrag
		);

		setTimeout(() => {
			this.hasDraggedClick = false;
		}, 100);
	}

	handleOnCanvasClick(event: MouseEvent) {
		if (
			this.hasMovedDragged ||
			this.hasDraggedClick ||
			this.keysCurrentlyPressed.includes('Space') ||
			this.isMultiselecting ||
			!!this.activeConnectionDrawing ||
			this.ignoreNextClick
		) {
			// The click is over, so even if it dragged, we reset the flag
			this.hasDraggedClick = false;
			this.ignoreNextClick = false;
			return;
		}

		if (this.activeConnectionDrawing) {
			this.activeConnectionDrawing.remove();
		}

		if (this.lastConnectionDrawing) {
			this.lastConnectionDrawing.remove();
		}

		this.persistScroll();

		const canvasRect = this.canvasElement.getBoundingClientRect();

		// Scale the coordinates of the click event location to the current scale
		const mouseX =
			event.clientX / this.currentScale -
			canvasRect.left / this.currentScale;
		const mouseY =
			event.clientY / this.currentScale -
			canvasRect.top / this.currentScale;

		const animatedElement = document.createElement('div');
		animatedElement.className = 'canvas-click-effect';
		animatedElement.style.top = event.clientY + 'px';
		animatedElement.style.left = event.clientX + 'px';
		document.body.appendChild(animatedElement);

		animatedElement.addEventListener(
			'animationend',
			function () {
				animatedElement.parentElement.removeChild(animatedElement);
			}.bind(this)
		);

		this.hasOnceInSessionClickedCanvas = true;

		if (this.tooltipTimeout) {
			clearTimeout(this.tooltipTimeout);
		}

		if (this.onClickCallback) {
			const resolvedCoordinates: ICoordinates = {
				x: mouseX,
				y: mouseY
			};

			this.onClickCallback(resolvedCoordinates, event);
		}

		this.unselectAll();

		// Certain actions can cause the browser to scroll,
		// so we make sure to remove any unwanted automatic scrolling by restoring the position to what it was before the click
		this.restoreScroll();
	}

	handleOnCanvasRightClick(event: MouseEvent) {
		if (
			this.hasMovedDragged ||
			this.hasDraggedClick ||
			this.keysCurrentlyPressed.includes('Space') ||
			this.isMultiselecting ||
			!!this.activeConnectionDrawing ||
			this.ignoreNextClick
		) {
			// The click is over, so even if it dragged, we reset the flag
			this.hasDraggedClick = false;
			this.ignoreNextClick = false;
			return;
		}

		if (this.activeConnectionDrawing) {
			this.activeConnectionDrawing.remove();
		}

		if (this.lastConnectionDrawing) {
			this.lastConnectionDrawing.remove();
		}

		this.persistScroll();

		const canvasRect = this.canvasElement.getBoundingClientRect();

		// Scale the coordinates of the click event location to the current scale
		const mouseX =
			event.clientX / this.currentScale -
			canvasRect.left / this.currentScale;
		const mouseY =
			event.clientY / this.currentScale -
			canvasRect.top / this.currentScale;

		const animatedElement = document.createElement('div');
		animatedElement.className = 'canvas-click-effect';
		animatedElement.style.top = event.clientY + 'px';
		animatedElement.style.left = event.clientX + 'px';
		document.body.appendChild(animatedElement);

		animatedElement.addEventListener(
			'animationend',
			function () {
				animatedElement.parentElement.removeChild(animatedElement);
			}.bind(this)
		);

		this.hasOnceInSessionClickedCanvas = true;

		if (this.tooltipTimeout) {
			clearTimeout(this.tooltipTimeout);
		}

		if (this.onRightClickCallback) {
			const resolvedCoordinates: ICoordinates = {
				x: mouseX,
				y: mouseY
			};

			this.onRightClickCallback(resolvedCoordinates, event);
		}

		this.unselectAll();

		// Certain actions can cause the browser to scroll,
		// so we make sure to remove any unwanted automatic scrolling by restoring the position to what it was before the click
		this.restoreScroll();
	}

	handleOnCanvasDoubleClick(event: MouseEvent) {
		if (
			this.hasMovedDragged ||
			this.hasDraggedClick ||
			this.keysCurrentlyPressed.includes('Space') ||
			this.isMultiselecting ||
			!!this.activeConnectionDrawing ||
			this.ignoreNextClick
		) {
			this.hasDraggedClick = false;
			this.ignoreNextClick = false;
			return;
		}

		if (this.activeConnectionDrawing) {
			this.activeConnectionDrawing.remove();
		}

		if (this.lastConnectionDrawing) {
			this.lastConnectionDrawing.remove();
		}

		this.persistScroll();

		const canvasRect = this.canvasElement.getBoundingClientRect();

		// Scale the coordinates of the click event location to the current scale
		const mouseX =
			event.clientX / this.currentScale -
			canvasRect.left / this.currentScale;
		const mouseY =
			event.clientY / this.currentScale -
			canvasRect.top / this.currentScale;

		const animatedElement = document.createElement('div');
		animatedElement.className = 'canvas-click-effect';
		animatedElement.style.top = event.clientY + 'px';
		animatedElement.style.left = event.clientX + 'px';
		document.body.appendChild(animatedElement);

		animatedElement.addEventListener(
			'animationend',
			function () {
				animatedElement.parentElement.removeChild(animatedElement);
			}.bind(this)
		);

		this.hasOnceInSessionClickedCanvas = true;

		if (this.tooltipTimeout) {
			clearTimeout(this.tooltipTimeout);
		}

		if (this.onDoubleClickCallback) {
			const resolvedCoordinates: ICoordinates = {
				x: mouseX,
				y: mouseY
			};

			this.onDoubleClickCallback(resolvedCoordinates, event);
		}

		this.unselectAll();
		this.restoreScroll();
	}

	setupClick() {
		this.canvasElement.addEventListener(
			'mousedown',
			this.handleOnClickMouseDown
		);

		document.addEventListener('mouseup', this.handleOnClickMouseUp);

		this.canvasElement.addEventListener('click', this.handleOnCanvasClick);
		this.canvasElement.addEventListener(
			'contextmenu',
			this.handleOnCanvasRightClick
		);
		this.canvasElement.addEventListener(
			'dblclick',
			this.handleOnCanvasDoubleClick
		);
	}

	handleOnCursorTooltipMouseMove(e: MouseEvent) {
		const canvasRect = this.canvasElement.getBoundingClientRect();

		const mouseX =
			e.clientX / this.currentScale - canvasRect.left / this.currentScale;
		const mouseY =
			e.clientY / this.currentScale - canvasRect.top / this.currentScale;

		this.tooltip.style.left = mouseX - 10 + 'px';
		this.tooltip.style.top = mouseY - 30 + 'px';

		if (!!this.userSetTooltipText) {
			return;
		}

		// If the user hasn't moved the mouse for a while, show the tooltip
		if (this.tooltipTimeout) {
			clearTimeout(this.tooltipTimeout);

			setTimeout(() => {
				this._hideTooltip();
			}, 350);
		}

		// Make the delay bigger after the first tooltip has been shown
		const delayMs = this.hasShownTooltip ? 2700 : 700;

		this.tooltipTimeout = setTimeout(() => {
			// Don't show it if we are selecting, clicking, or dragging
			if (
				this.isSelecting ||
				this.hasDraggedClick ||
				this.hasMovedDragged
			) {
				return;
			}

			// Don't show it if we are over another element
			if (e.target !== this.canvasElement) {
				return;
			}

			// Don't show it has cancelled since the timeout started
			if (this.cancelShowTooltip) {
				this.cancelShowTooltip = false;
				return;
			}

			// Don't show if it has shown it before
			if (
				!this.hasShownClickTooltip &&
				!this.hasOnceInSessionClickedCanvas
			) {
				this._showTooltip(
					`Double-click or right-click anywhere to create a new element`
				);
				this.hasShownClickTooltip = true;
				return;
			} else if (!this.hasShownDragElementTooltip) {
				this._showTooltip(`Click on an element and drag to move it`);
				this.hasShownDragElementTooltip = true;
				return;
			} else if (
				!this.hasShownPanTooltip &&
				!this.hasOnceInSessionMovedInCanvas
			) {
				this._showTooltip(
					`Hold space and drag, or click the mouse wheel and drag, to move around the canvas`
				);
				this.hasShownPanTooltip = true;
				return;
			} else if (
				!this.hasShownZoomTooltip &&
				!this.hasOnceInSessionZoomed
			) {
				this._showTooltip(
					`You can zoom in and out with the mouse wheel or touchpad`
				);
				this.hasShownZoomTooltip = true;
				return;
			} else if (
				!this.hasShownSelectTooltip &&
				!this.hasOnceInSessionSelected
			) {
				this._showTooltip(
					`Click on the canvas and drag to select multiple elements`
				);
				this.hasShownSelectTooltip = true;
				return;
			}
		}, delayMs);
	}

	handleOnCursorTooltipMouseLeave(e: MouseEvent) {
		// Only cancel the tooltip if the target is not a child of the canvas
		if (
			e.relatedTarget &&
			this.canvasElement.contains(e.relatedTarget as Node)
		) {
			return;
		}

		this.cancelShowTooltip = true;

		if (this.tooltipTimeout) {
			clearTimeout(this.tooltipTimeout);
		}
		this._hideTooltip();
	}

	private _showTooltip(text: string) {
		if (
			!!this.userSetTooltipText &&
			this.userSetTooltipText !== this.tooltip.innerText
		) {
			this.tooltip.hidden = false;
			this.tooltip.innerText = this.userSetTooltipText;
			return;
		}

		this.tooltip.hidden = false;
		this.tooltip.innerText = text;
	}

	private _hideTooltip() {
		if (!this.userSetTooltipText) {
			this.tooltip.hidden = true;
		}
	}

	showTooltip(id: string, text: string) {
		this.activeUserSetTooltipId = id;
		this.userSetTooltipText = text;
		this._showTooltip(text);

		// Clear any existing timeout, then set a timeout to hide the tooltip after a while
		if (this.tooltipTimeout) {
			clearTimeout(this.tooltipTimeout);
		}

		this.tooltipTimeout = setTimeout(() => {
			this.hideTooltip(id);
		}, 5000);
	}

	hideTooltip(id: string | null = null) {
		if (
			!this.activeUserSetTooltipId ||
			this.activeUserSetTooltipId === id ||
			id === null
		) {
			this.activeUserSetTooltipId = null;
			this.userSetTooltipText = null;
			this._hideTooltip();
		}
	}

	/*
		Creates a tooltip that follows the mouse cursor
		Tooltip is hidden by default and show up on certain occasions to guide the user
		If the user hasn't moved the mouse for a while, the tooltip will show up
	*/
	setupCursorTooltip() {
		this.tooltip = document.createElement('div');
		this.tooltip.className = 'canvas-tooltip';
		this.tooltip.hidden = true;
		this.tooltip.style.position = 'absolute';
		this.tooltip.style.zIndex = '100';
		this.tooltip.style.pointerEvents = 'none';

		// Add style properties to the tooltip element
		this.tooltip.style.background = '#222';
		this.tooltip.style.color = 'white';
		this.tooltip.style.padding = '5px';
		this.tooltip.style.borderRadius = '4px';
		this.tooltip.style.fontSize = '90%';

		// Add slow fade in animation
		this.tooltip.style.animation = 'fadein 0.3s';

		this.canvasElement.appendChild(this.tooltip);

		this.canvasElement.addEventListener(
			'mousemove',
			this.handleOnCursorTooltipMouseMove
		);
		this.canvasElement.addEventListener(
			'mouseleave',
			this.handleOnCursorTooltipMouseLeave
		);
	}

	selectionReset() {
		const resolvedX1 = Math.min(
			this.selectionBoxLeft,
			this.selectionBoxRight
		);
		const resolvedX2 = Math.max(
			this.selectionBoxLeft,
			this.selectionBoxRight
		);
		const resolvedY1 = Math.min(
			this.selectionBoxTop,
			this.selectionBoxBottom
		);
		const resolvedY2 = Math.max(
			this.selectionBoxTop,
			this.selectionBoxBottom
		);
		this.selectionBox.style.left = resolvedX1 + 'px';
		this.selectionBox.style.top = resolvedY1 + 'px';
		this.selectionBox.style.width = resolvedX2 - resolvedX1 + 'px';
		this.selectionBox.style.height = resolvedY2 - resolvedY1 + 'px';
	}

	handleOnSelectionMouseDown(e: MouseEvent) {
		if (
			this.hasMovedDragged ||
			this.keysCurrentlyPressed.includes('Space') ||
			!this.selectionEnabled
		) {
			return;
		}

		// Check if this mouse down event is over an element that is not the canvas
		// If so, don't start the selection
		if (e.target !== this.canvasElement) {
			return;
		}

		this.selectionBox.hidden = false;

		const canvasRect = this.canvasElement.getBoundingClientRect();

		const mouseX =
			e.clientX / this.currentScale - canvasRect.left / this.currentScale;
		const mouseY =
			e.clientY / this.currentScale - canvasRect.top / this.currentScale;

		this.selectionBoxLeft = mouseX;
		this.selectionBoxTop = mouseY;

		this.selectionBoxRight = this.selectionBoxLeft;
		this.selectionBoxBottom = this.selectionBoxTop;

		this.selectionReset();

		document.addEventListener('mousemove', this.handleOnSelectionMouseMove);
	}

	handleOnSelectionMouseMove(e: MouseEvent) {
		if (
			this.hasMovedDragged ||
			this.keysCurrentlyPressed.includes('Space')
		) {
			return;
		}

		// Disabling text selection while selecting
		document.body.style.userSelect = 'none';
		// Webkit user select
		document.body.style.webkitUserSelect = 'none';

		document.body.style.cursor = 'crosshair';
		this.canvasElement.classList.add('selection-cursor');

		this.isSelecting = true;
		this.startAutoPanLoop();

		const canvasRect = this.canvasElement.getBoundingClientRect();

		const mouseX =
			e.clientX / this.currentScale - canvasRect.left / this.currentScale;
		const mouseY =
			e.clientY / this.currentScale - canvasRect.top / this.currentScale;

		this.selectionBoxRight = mouseX;
		this.selectionBoxBottom = mouseY;

		this.onRecalculateSelection(e);
	}

	clearSelection() {
		document.body.style.userSelect = 'auto';
		// Webkit user select
		document.body.style.webkitUserSelect = 'auto';
		document.body.style.cursor = 'auto';
		this.canvasElement.classList.remove('selection-cursor');

		this.selectionBox.hidden = true;

		document.removeEventListener(
			'mousemove',
			this.handleOnSelectionMouseMove
		);

		this.selectedInCurrentSelectionAction = [];
		this.selectionBoxLeft = 0;
		this.selectionBoxTop = 0;
		this.selectionBoxRight = 0;
		this.selectionBoxBottom = 0;
	}

	endSelection(e: MouseEvent) {
		this.clearSelection();

		if (this.onSelectEndCallback) {
			this.onSelectEndCallback(e);
		}

		setTimeout(() => {
			this.isSelecting = false;
		}, 100);
	}

	onRecalculateSelection(e: MouseEvent) {
		const resolvedLeft = Math.min(
			this.selectionBoxLeft,
			this.selectionBoxRight
		);
		const resolvedRight = Math.max(
			this.selectionBoxLeft,
			this.selectionBoxRight
		);
		const resolvedTop = Math.min(
			this.selectionBoxTop,
			this.selectionBoxBottom
		);
		const resolvedBottom = Math.max(
			this.selectionBoxTop,
			this.selectionBoxBottom
		);

		this.selectionBox.style.left = resolvedLeft + 'px';
		this.selectionBox.style.top = resolvedTop + 'px';
		this.selectionBox.style.width = resolvedRight - resolvedLeft + 'px';
		this.selectionBox.style.height = resolvedBottom - resolvedTop + 'px';

		this.calculateObjectsSelection({
			left: resolvedLeft,
			top: resolvedTop,
			right: resolvedRight,
			bottom: resolvedBottom
		});

		this.hasOnceInSessionSelected = true;

		if (this.tooltipTimeout) {
			clearTimeout(this.tooltipTimeout);
		}

		if (this.onSelectingCallback) {
			this.onSelectingCallback(e);
		}
	}

	// Creates a selection box on the canvas as the user clicks and drags
	setupSelection() {
		this.selectionBox = document.createElement('div');
		this.selectionBox.id = 'selection-box--' + this.id;
		this.selectionBox.style.position = 'absolute';
		this.selectionBox.style.border = '1px dotted #000';
		this.selectionBox.style.pointerEvents = 'none';
		this.selectionBox.hidden = true;
		this.selectionBox.style.zIndex = '100';

		this.canvasElement.appendChild(this.selectionBox);

		this.selectionBoxLeft = 0;
		this.selectionBoxTop = 0;
		this.selectionBoxRight = 0;
		this.selectionBoxBottom = 0;

		document.addEventListener('mousedown', this.handleOnSelectionMouseDown);
		document.addEventListener('mouseup', this.endSelection);
	}

	// This method tears down the event listeners and other state set up by the 'setupSelection' method
	// Including the events set up by event listeners themselfes if any, and if they don't handle their own cleanup
	teardownSelection() {
		this.selectionBox.remove();
		this.selectionBox = null;
		this.selectionBoxLeft = 0;
		this.selectionBoxTop = 0;
		this.selectionBoxRight = 0;
		this.selectionBoxBottom = 0;

		document.removeEventListener(
			'mousedown',
			this.handleOnSelectionMouseDown
		);
		document.removeEventListener('mouseup', this.endSelection);
		document.removeEventListener(
			'mousemove',
			this.handleOnSelectionMouseMove
		);
	}

	teardownClick() {
		this.canvasElement.removeEventListener(
			'click',
			this.handleOnCanvasClick
		);
		this.canvasElement.removeEventListener(
			'contextmenu',
			this.handleOnCanvasRightClick
		);
		document.removeEventListener('mouseup', this.handleOnClickMouseUp);
		this.canvasElement.removeEventListener(
			'mousedown',
			this.handleOnClickMouseDown
		);
		document.removeEventListener('mousemove', this.onClickAndDrag);
	}

	teardownUndoRedo() {
		removeUndoEventListener(this.id);
	}

	teardownCopyPaste() {
		document.removeEventListener('copy', this.handleCopy);
		document.removeEventListener('paste', this.handlePaste);
	}

	teardownZoom() {
		this.canvasElement.removeEventListener(
			'mousewheel',
			this.handleOnZoomMouseWheel as EventListener
		);
		this.canvasElement.removeEventListener(
			'DOMMouseScroll' as any,
			this.handleOnZoomMouseWheel as EventListener
		);
		this.canvasElement.removeEventListener(
			'wheel',
			this.handleOnZoomMouseWheel as EventListener
		);
	}

	teardownCursorTooltip() {
		this.tooltip.remove();
		this.canvasElement.removeEventListener(
			'mousemove',
			this.handleOnCursorTooltipMouseMove
		);
		this.canvasElement.removeEventListener(
			'mouseleave',
			this.handleOnCursorTooltipMouseLeave
		);
	}

	teardownShortcuts() {
		window.removeEventListener('keydown', this.onGeneralKeyDown);
		window.removeEventListener('keyup', this.onGeneralKeyUp);
		window.removeEventListener('blur', this.onWindowBlur);
	}

	teardownViewportResize() {
		window.removeEventListener('resize', this.handleResize);
	}

	draggingOnCanvas(event: MouseEvent) {
		this.hasMovedDragged = true;
		this.hasOnceInSessionMovedInCanvas = true;
		this.canvasElement.style.cursor = 'grabbing';

		const movementX = event.clientX - this.originalMouseDownX;
		const movementY = event.clientY - this.originalMouseDownY;

		// If we want to scroll to position x 1200, we pass that as the value to the scrollTo function
		// If we want to scroll by how much the mouse is moving, we need to capture how much the mouse moved, and add it to the original scroll value
		const scrollToX = this.originalScrollXLocation - movementX;
		const scrollToY = this.originalScrollYLocation - movementY;

		this.scrollTo(scrollToX, scrollToY);

		this.persistScroll();

		if (this.onPanCallback) {
			this.onPanCallback(event);
		}
	}

	startMoveCapture(e: MouseEvent) {
		// We set the original coordinate of where the mouse was clicked and where scrolling location was
		this.originalMouseDownX = e.clientX;
		this.originalMouseDownY = e.clientY;
		this.originalScrollXLocation =
			document.documentElement.scrollLeft || document.body.scrollLeft;
		this.originalScrollYLocation =
			document.documentElement.scrollTop || document.body.scrollTop;

		this.canvasElement.addEventListener('mousemove', this.draggingOnCanvas);
	}

	endMoveCapture(e: MouseEvent) {
		this.originalMouseDownX = null;
		this.originalMouseDownY = null;
		this.originalScrollXLocation = null;
		this.originalScrollYLocation = null;
		this.canvasElement.style.cursor = 'auto';

		this.canvasElement.removeEventListener(
			'mousemove',
			this.draggingOnCanvas
		);

		setTimeout(() => {
			this.hasMovedDragged = false;
		}, 100);
	}

	zoomTo(_newScale: number, duration: number = 300) {
		// 1) Clamp the target scale to your min/max limits
		let finalScale = _newScale;
		if (finalScale < this.minScale) finalScale = this.minScale;
		if (finalScale > this.maxScale) finalScale = this.maxScale;

		// If we are already at the target scale, do nothing
		if (this.currentScale === finalScale) return;

		// 2) Snapshot old scale and old scroll position
		const startScale = this.currentScale;
		const startScrollX =
			window.scrollX ?? document.documentElement.scrollLeft;
		const startScrollY =
			window.scrollY ?? document.documentElement.scrollTop;

		// 3) Grab the canvas’s bounding‐box BEFORE changing its CSS transform
		const canvasRect = this.canvasElement.getBoundingClientRect();

		// 4) Pick the VIEWPORT CENTER
		const viewportCenterX = window.innerWidth / 2;
		const viewportCenterY = window.innerHeight / 2;

		// 5) Compute the “center‐in‐canvas” coordinate (pre‐scale)
		const centerX_inCanvas =
			(viewportCenterX - canvasRect.left) / startScale;
		const centerY_inCanvas =
			(viewportCenterY - canvasRect.top) / startScale;

		// 6) Animation Setup
		const startTime = performance.now();

		// Easing function for a smoother feel (Ease-Out Cubic)
		const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

		const animate = (currentTime: number) => {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);

			// Apply easing to the progress
			const easeProgress = easeOutCubic(progress);

			// Calculate the scale for this specific frame
			const currentInterpolatedScale =
				startScale + (finalScale - startScale) * easeProgress;

			// Calculate how much to shift scroll for this specific frame
			const scaleDiff = currentInterpolatedScale - startScale;
			const currentScrollX = startScrollX + centerX_inCanvas * scaleDiff;
			const currentScrollY = startScrollY + centerY_inCanvas * scaleDiff;

			// Apply the new scale and scroll together
			this.applyScale(currentInterpolatedScale);
			this.scrollTo(currentScrollX, currentScrollY);

			if (progress < 1) {
				// Continue animation if not done
				requestAnimationFrame(animate);
			} else {
				// Animation finished: ensure we snap exactly to the final target values
				// to avoid floating point inaccuracies
				this.applyScale(finalScale);
				const finalScaleDiff = finalScale - startScale;
				this.scrollTo(
					startScrollX + centerX_inCanvas * finalScaleDiff,
					startScrollY + centerY_inCanvas * finalScaleDiff
				);

				// Mark that we have zoomed at least once (for any tooltip logic)
				this.hasOnceInSessionZoomed = true;
				if (this.tooltipTimeout) {
					clearTimeout(this.tooltipTimeout);
				}
			}
		};

		// Kick off the animation
		requestAnimationFrame(animate);
	}

	zoomBy(modifyBy: number, duration: number = 300) {
		// Calculate the target scale based on the current scale
		const targetScale = this.currentScale + modifyBy;

		// Delegate the actual animation and viewport centering to our new zoomTo method
		this.zoomTo(targetScale, duration);
	}

	rawZoomBy(modifyBy: number, targetX?: number, targetY?: number) {
		// 2) Snapshot old scale and old scroll position:
		const oldScale = this.currentScale;
		const scrollX0 = window.scrollX ?? document.documentElement.scrollLeft;
		const scrollY0 = window.scrollY ?? document.documentElement.scrollTop;

		// 3) Grab the canvas’s bounding‐box BEFORE changing its CSS transform
		const canvasRect = this.canvasElement.getBoundingClientRect();

		let newScale = oldScale + modifyBy;

		if (newScale < this.minScale) newScale = this.minScale;
		if (newScale > this.maxScale) newScale = this.maxScale;

		// 4) Instead of using viewport center, pick the cursor position if provided:
		const focusX = targetX ?? window.innerWidth / 2;
		const focusY = targetY ?? window.innerHeight / 2;

		// 5) Compute the “center‐in‐canvas” coordinate (pre‐scale):
		//    i.e. which point in the canvas lies at the focus point today?
		//    (We subtract canvasRect.left/top, then un‐scale by oldScale.)
		const focusX_inCanvas = (focusX - canvasRect.left) / oldScale;
		const focusY_inCanvas = (focusY - canvasRect.top) / oldScale;

		// 6) Calculate how much to shift scroll so that
		//    (focusX_inCanvas, focusY_inCanvas) remains at focus point:
		//    scrollX1 = scrollX0 + focusX_inCanvas * (newScale − oldScale)
		//    scrollY1 = scrollY0 + focusY_inCanvas * (newScale − oldScale)
		const scaleDiff = newScale - oldScale;
		const scrollX1 = scrollX0 + focusX_inCanvas * scaleDiff;
		const scrollY1 = scrollY0 + focusY_inCanvas * scaleDiff;

		// 7) Actually apply the new scale to the canvas container:
		this.applyScale(newScale);

		// 8) Finally, immediately scroll so that the viewport‐center point
		//    stays in place on screen (the canvas will expand/contract around it).
		this.scrollTo(scrollX1, scrollY1);

		// 9) Mark that we have zoomed at least once (for any tooltip logic):
		this.hasOnceInSessionZoomed = true;
		if (this.tooltipTimeout) {
			clearTimeout(this.tooltipTimeout);
		}
	}

	// -----------------------------------------------------------
	// Zoom handler that either pans (integer deltaY) or zooms
	// around the CENTER of the viewport (fractional deltaY).
	// -----------------------------------------------------------
	// Find this method in your class
	handleOnZoomMouseWheel(e: WheelEvent) {
		// Prevent the browser’s default scroll/zoom
		e.preventDefault();

		// 1. Check if it's a Pinch (Trackpad) or Ctrl+Scroll (Mouse)
		// We also check metaKey to support Cmd+Scroll on Mac
		// Instead of checking for integers, we check for the ctrlKey.
		// Chrome automatically sets e.ctrlKey to true during a Trackpad pinch gesture.
		// It is false during a standard two-finger scroll (pan).
		const isZoomingMovement =
			e.ctrlKey || e.metaKey || Math.abs(e.deltaY) > 150;

		// 2. Otherwise, it is a Pan
		const isPanningMovement = !isZoomingMovement;

		if (isPanningMovement) {
			this.isMovingCanvas = true;
			const canvasElemBox = this.canvasElement.getBoundingClientRect();

			// The modern default standard is to invert the raw delta from the browser
			let deltaX = -e.deltaX;
			let deltaY = -e.deltaY;

			// If the user selects to "invert" the scroll, they actually revert to the original Elyx behaviour
			if (this.invertScrollDirection) {
				deltaX = -deltaX;
				deltaY = -deltaY;
			}

			let scrollToX = Canvas.getCurrentScrollX() - deltaX;
			let scrollToY = Canvas.getCurrentScrollY() - deltaY;

			const isMovingLeftMoreThanRemainingSpace =
				canvasElemBox.left + deltaX > 0;
			const isMovingUpMoreThanRemainingSpace =
				canvasElemBox.top + deltaY > 0;

			const isMovingLeft = deltaX > 0;
			const isMovingUp = deltaY > 0;

			if (isMovingLeftMoreThanRemainingSpace) {
				scrollToX = Canvas.getCurrentScrollX() + canvasElemBox.left;
			} else if (canvasElemBox.left > 0 && isMovingLeft) {
				scrollToX = Canvas.getCurrentScrollX();
			}

			if (isMovingUpMoreThanRemainingSpace) {
				scrollToY = Canvas.getCurrentScrollY() + canvasElemBox.top;
			} else if (canvasElemBox.top > 0 && isMovingUp) {
				scrollToY = Canvas.getCurrentScrollY();
			}

			this.scrollTo(scrollToX, scrollToY);

			// clear any pending “end” callback
			if (this.wheelEndTimeout !== null) {
				window.clearTimeout(this.wheelEndTimeout);
			}
			// and schedule a new one
			this.wheelEndTimeout = window.setTimeout(() => {
				this.isMovingCanvas = false;
				this.wheelEndTimeout = null;
			}, 150);

			return;
		}

		// --- ZOOM LOGIC ---
		// Note: e.wheelDelta is deprecated. Ideally use e.deltaY for the calculation below,
		// but if this calculation works for you, you can keep it.
		// Standardizing on -e.deltaY usually provides smoother results across browsers.
		const delta = Math.max(
			-1,
			// @ts-ignore
			Math.min(1, e.wheelDelta || -e.detail || -e.deltaY)
		);

		const step = 1 + delta / 1000;
		const modifyBy = (step - 1) * 20;

		this.rawZoomBy(modifyBy, e.clientX, e.clientY);
	}

	setupZoom() {
		this.canvasElement.addEventListener(
			'mousewheel',
			this.handleOnZoomMouseWheel as EventListener,
			{
				passive: false
			}
		);
		this.canvasElement.addEventListener(
			'DOMMouseScroll',
			this.handleOnZoomMouseWheel as EventListener,
			{
				passive: false
			}
		);
		this.canvasElement.addEventListener(
			'wheel',
			this.handleOnZoomMouseWheel as EventListener,
			{
				passive: false
			}
		);
	}

	deleteSelectedObjects() {
		// Attempt to remove the selected connections first.
		const connectionsThatFailedToDelete: Connection[] = [];
		const successfullyDeletedConnections: Connection[] = [];
		const deletedConnectionIds: Set<string> = new Set();

		if (this.selectedConnections.length > 0) {
			[...this.selectedConnections].forEach((connection) => {
				const isSourceBeingDeleted = this.selectedObjects.includes(
					connection.sourceParentObject as any
				);
				const isTargetBeingDeleted = this.selectedObjects.includes(
					connection.targetParentObject as any
				);
				const force = isSourceBeingDeleted || isTargetBeingDeleted;

				const res = connection.remove({
					force,
					batchDelete:
						this.selectedConnections.length > 1 ||
						!!this.selectedObjects.length
				});
				if (res && res.deleted === false) {
					connectionsThatFailedToDelete.push(connection);
				} else {
					successfullyDeletedConnections.push(connection);
					deletedConnectionIds.add(connection.id);
				}
			});
		}

		// Compile list of objects who will receive changes.
		// These are all selected objects but also non-selected objects whos connections are selected
		const nonDeletedObjectsWithDeletedConnections: (
			| DraggableObject
			| CanvasObject
		)[] = [];

		if (successfullyDeletedConnections.length > 0) {
			successfullyDeletedConnections.forEach((connection) => {
				if (
					!nonDeletedObjectsWithDeletedConnections.includes(
						connection.sourceParentObject
					)
				) {
					nonDeletedObjectsWithDeletedConnections.push(
						connection.sourceParentObject
					);
				}

				if (
					!nonDeletedObjectsWithDeletedConnections.includes(
						connection.targetParentObject
					)
				) {
					nonDeletedObjectsWithDeletedConnections.push(
						connection.targetParentObject
					);
				}
			});
		}

		const valuesSoonToChange = this.toUndoableValues([
			...(this.selectedObjects as (DraggableObject | CanvasObject)[]),
			...nonDeletedObjectsWithDeletedConnections
		]);

		// Capture the values before the change, so we know what to revert to if we undo
		this.captureValuesBeforeUndoableChange(valuesSoonToChange);

		// Capture the deletion of this objects as an undoable change
		const deletedObjectsValues = this.toUndoableValues(
			this.selectedObjects as any as DraggableObject[],
			{
				shouldMarkAsDeleted: true
			}
		);

		const nonDeletedObjectsWithDeletedConnectionsValues: IDraggableUndoableValue[] =
			this.toUndoableValues(nonDeletedObjectsWithDeletedConnections, {
				deletedConnections: Array.from(deletedConnectionIds)
			});

		const allChanges = [
			...deletedObjectsValues,
			...nonDeletedObjectsWithDeletedConnectionsValues
		];

		this.captureUndoableChange(allChanges);

		// Remove their parents' connections array
		successfullyDeletedConnections.forEach((connection) => {
			connection.sourceParentObject.connections =
				connection.sourceParentObject.connections.filter(
					(conn) => conn !== connection
				);
			connection.targetParentObject.connections =
				connection.targetParentObject.connections.filter(
					(conn) => conn !== connection
				);
		});

		// Reset the selected connections array, keeping the ones that failed
		this.selectedConnections = connectionsThatFailedToDelete;

		if (this.selectedObjects.length === 0) {
			return;
		}

		this.removeDraggableObjects(
			this.selectedObjects as any as DraggableObject[]
		);

		this.onDeleteCallback?.(this.selectedObjects as any as CanvasObject[]);

		this.selectedObjects = [];
	}

	onGeneralKeyDown(e: KeyboardEvent) {
		this.addKeyPressTracking(e.code);

		switch (e.code) {
			case 'Escape': {
				this.selectedObjects.forEach((canvasObject) => {
					canvasObject.unselect();
				});

				this.selectedConnections.forEach((connection) => {
					connection.unselect();
				});

				this.isSelecting = false;
				this.selectedObjects = [];
				this.selectedConnections = [];

				// Reset "hold spacebar" state in case it got stuck
				this.removeKeyPressTracking('Space');

				// Clear all currently pressed keys
				this.keysCurrentlyPressed = [];
				this.canvasElement.style.cursor = 'auto';
				this.canvasElement.removeEventListener(
					'mousedown',
					this.startMoveCapture
				);
				document.removeEventListener('mouseup', this.endMoveCapture);
				this.endMoveCapture(new MouseEvent('mouseup'));

				this.onExitCallback?.();
				break;
			}
			case 'Delete':
			case 'Backspace': {
				// Ignore the delete key if the user is typing in an input
				// And any other element that uses the keyboard when focused
				if (
					e.target instanceof HTMLInputElement ||
					e.target instanceof HTMLTextAreaElement ||
					e.target instanceof HTMLSelectElement
				) {
					return;
				}

				this.deleteSelectedObjects();

				break;
			}

			// Shift
			case 'ShiftLeft':
			case 'ShiftRight': {
				break;
			}
		}
	}

	removeDraggableObjectsFromIds(
		ids: string[],
		removeOptions: { quiet: boolean } = { quiet: false }
	) {
		const draggableObjects = this.draggableObjects.filter(
			(draggableObject) => {
				return ids.includes(draggableObject.id);
			}
		);

		this.removeDraggableObjects(draggableObjects, removeOptions);
	}

	// Loop over the given draggable objects and remove them from the state
	removeDraggableObjects(
		draggableObjects: DraggableObject[],
		removeOptions: { quiet: boolean } = { quiet: false }
	) {
		draggableObjects.forEach((draggableObject) => {
			draggableObject.remove(removeOptions);
		});

		this.draggableObjects = this.draggableObjects.filter(
			(object) => !draggableObjects.includes(object)
		);
	}

	removeDraggableObject(
		draggableObject: DraggableObject,
		removeOptions: { quiet: boolean } = { quiet: false }
	) {
		if (!draggableObject.deleted) {
			draggableObject.remove(removeOptions);
			this.draggableObjects = this.draggableObjects.filter(
				(object) => object !== draggableObject
			);
		}
	}

	removeDraggableObjectFromId(
		id: string,
		removeOptions: { quiet: boolean } = { quiet: false }
	) {
		const draggableObject = this.draggableObjects.find(
			(draggableObject) => draggableObject.id === id
		);

		if (draggableObject) {
			this.removeDraggableObject(draggableObject, removeOptions);
		}
	}

	onGeneralKeyUp(e: KeyboardEvent) {
		this.removeKeyPressTracking(e.code);

		switch (e.code) {
			// Shift
			case 'ShiftLeft':
			case 'ShiftRight': {
				if (this.canvasElement) {
					this.canvasElement.style.cursor = 'auto';
				}
				break;
			}
		}
	}

	onWindowBlur() {
		this.keysCurrentlyPressed = [];
	}

	setupShortcuts() {
		// On escape, unselect all objects
		window.addEventListener('keydown', this.onGeneralKeyDown);
		window.addEventListener('keyup', this.onGeneralKeyUp);
		window.addEventListener('blur', this.onWindowBlur);
	}



	setupCopyPaste() {
		// Copy event
		document.addEventListener('copy', this.handleCopy);

		// Paste event
		document.addEventListener('paste', this.handlePaste);
	}

	handleOnGlobalMouseMove(e: MouseEvent) {
		this.lastGlobalMouseMoveEvent = e;
	}

	startAutoPanLoop() {
		if (this.autoPanFrameId !== null) return;
		const loop = () => {
			this.autoPanFrameId = requestAnimationFrame(loop);
			this.doAutoPan();
		};
		this.autoPanFrameId = requestAnimationFrame(loop);
	}

	stopAutoPanLoop() {
		if (this.autoPanFrameId !== null) {
			cancelAnimationFrame(this.autoPanFrameId);
			this.autoPanFrameId = null;
		}
	}

	doAutoPan() {
		if (!this.lastGlobalMouseMoveEvent) return;

		// If neither dragging a selection box nor drawing a connection, stop.
		if (!this.isSelecting && !this.activeConnectionDrawing) {
			this.stopAutoPanLoop();
			return;
		}

		const e = this.lastGlobalMouseMoveEvent;
		const threshold = this.autoPanEdgeThreshold;
		let panX = 0;
		let panY = 0;

		if (e.clientX < threshold) panX = -this.autoPanSpeed;
		else if (e.clientX > window.innerWidth - threshold)
			panX = this.autoPanSpeed;

		if (e.clientY < threshold) panY = -this.autoPanSpeed;
		else if (e.clientY > window.innerHeight - threshold)
			panY = this.autoPanSpeed;

		if (panX !== 0 || panY !== 0) {
			window.scrollBy(panX, panY);

			// Re-trigger the active dragging function with the same event
			// Since the window scrolled, getBoundingClientRect() will yield new offsets
			// allowing the mouse coordinate inside canvas space to shift smoothly.
			if (this.isSelecting) {
				this.handleOnSelectionMouseMove(e);
			} else if (this.activeConnectionDrawing) {
				this.activeConnectionDrawing.onMouseMove(e);
			}
		}
	}

	handleResize() {
		this.draggableObjects.forEach((draggableObject) => {
			// draggableObject.connections.forEach((conn) => conn.connector.update());
			draggableObject.connections.forEach((conn) => conn.update());
		});
	}

	// Subscribe any actions that need to be triggered when the viewport is resized
	// Eg: recalculate position of relative elements to their anchors
	private setupViewportResize() {
		// Listen to document mouse move to track position
		document.addEventListener('mousemove', this.handleOnGlobalMouseMove);

		window.addEventListener('resize', this.handleResize);
	}

	private setup() {
		this.setupElement();

		// this.createTestObjectAtTopLeft();

		this.setupCanvasMovement();
		this.setupSelection();
		this.setupClick();
		this.setupZoom();
		this.setupCursorTooltip();
		this.setupShortcuts();
		this.setupUndoRedo();
		this.setupCopyPaste();
		this.setupViewportResize();

		this.ready = true;
	}

	destroy() {
		this.ready = false;

		this.teardownCanvasMovement();
		this.teardownSelection();
		this.teardownClick();
		this.teardownZoom();
		this.teardownUndoRedo();
		this.teardownCopyPaste();
		this.teardownShortcuts();
		this.teardownCursorTooltip();
		this.teardownViewportResize();

		this.offScaleChange();
		this.offPan();
		this.offSelecting();
		this.offSelectEnd();
		this.offCopy();
		this.offPaste();
		this.offShouldResolveOwnInitialScroll();

		this.canvasElement.remove();
		this.canvasElement = null;

		this.initialValues = [];
		this.draggableObjects = [];
		this.selectedObjects = [];
		this.selectedConnections = [];
		this.selectedInCurrentSelectionAction = [];
		this.keysCurrentlyPressed = [];
		this.elementClasses = [];
	}

	// Check if the anchor element is present,
	// Re-queries if it isn't
	getAnchorElement(): HTMLElement {
		if (this.anchorElement) {
			return this.anchorElement;
		}

		if (!this.anchorElementQuerySelector) {
			return null;
		}

		const anchorElement = document.getElementById(
			this.anchorElementQuerySelector
		);

		if (!anchorElement) {
			throw new Error('Anchor element not found');
		}

		return anchorElement;
	}

	setupElement() {
		// Based on the canvas id, read the scale from local storage
		const currentScale = localStorage.getItem(`canvas-${this.id}-scale`);

		if (!this.canvasElement) {
			// Create new 'div' element and store it in the canvasElement property
			// Then append it to the canvas anchor element if it isn't already there
			this.canvasElement = document.createElement('div');
			this.canvasElement.id = this.id;

			// Add user classes to element
			this.elementClasses.forEach((className) => {
				this.canvasElement.classList.add(className);
			});

			if (currentScale) {
				let newScale = parseFloat(currentScale);

				this.applyScale(newScale);
				// if (newScale < 0.2) {
				// 	newScale = 0.2;
				// }

				// this.currentScale = newScale;
				// this.canvasElement.style.transform = `scale(${this.currentScale.toString()})`;
			} else {
				this.currentScale = 1;
			}

			// Set up the canvas element
			this.canvasElement.style.touchAction = 'pinch-zoom pan-x pan-y';
			this.canvasElement.style.position = 'absolute';
			this.canvasElement.style.width = '40000px';
			this.canvasElement.style.height = '40000px';
			this.canvasElement.style.transformOrigin = '0 0';

			let newScale = this.currentScale;

			this.applyScale(newScale);
		}

		// Append the canvas element to the anchor element
		const anchorElement = this.getAnchorElement();

		// If the anchor element is not present, throw an error
		if (!anchorElement) {
			throw new Error('Anchor element not found');
		}

		// Check if element is already a child of the anchor element
		// If not, append it
		if (!anchorElement.contains(this.canvasElement)) {
			anchorElement.appendChild(this.canvasElement);
		}

		const persistedScrollLocally = this.retrieveScrollFromLocalStorage();

		if (
			persistedScrollLocally &&
			persistedScrollLocally.x &&
			persistedScrollLocally.y
		) {
			this.naiveUnscaledDocumentScrollX = persistedScrollLocally.x;
			this.naiveUnscaledDocumentScrollY = persistedScrollLocally.y;
		} else {
			this.onShouldResolveOwnInitialScrollCallback?.();
		}

		this.restoreScroll();
	}

	static scaleScrollValue(scrollValue: number, scale: number) {
		return scrollValue * scale;
	}

	// Capture and persist the current document element real scroll value into the state
	persistScroll() {
		const scrollX = Canvas.getCurrentScrollX();
		const scrollY = Canvas.getCurrentScrollY();

		this.naiveUnscaledDocumentScrollX = scrollX;
		this.naiveUnscaledDocumentScrollY = scrollY;

		this.persistScrollToLocalStorage();
	}

	// This method restores the scroll value to what it was before an action was performed
	// Default HTML behavior scrolls automatically on many occations, so we need to restore the scroll value
	restoreScroll() {
		window.scrollTo(
			this.naiveUnscaledDocumentScrollX,
			this.naiveUnscaledDocumentScrollY
		);
	}

	getObjectCoords(
		canvasObject: CanvasObject | DraggableObject,
		altCoordinates?: ICoordinates
	) {
		const top = parseInt(canvasObject.element.style.top);
		const left = parseInt(canvasObject.element.style.left);

		const box = canvasObject.element.getBoundingClientRect();

		const scaledHeight = box.height / this.currentScale;
		const scaledWidth = box.width / this.currentScale;

		const bottom = top + scaledHeight;
		const right = left + scaledWidth;

		if (altCoordinates) {
			const scaledTop = altCoordinates.y;
			const scaledLeft = altCoordinates.x;

			const top = scaledTop;
			const left = scaledLeft;
			const bottom = top + scaledHeight;
			const right = left + scaledWidth;

			return { top, left, bottom, right };
		}

		return { top, left, bottom, right };
	}

	resolveAreasOverlap(areaA: IArea, areaB: IArea): boolean {
		// Y axis (verticall) overlap checks

		const verticallyStartsOutEndsIn =
			areaB.top <= areaA.top && areaB.bottom >= areaA.top;

		const verticallyStartsInEndsIn =
			areaB.top >= areaA.top && areaB.bottom <= areaA.bottom;

		const verticallyStartsInEndsOut =
			areaB.top <= areaA.bottom && areaB.bottom >= areaA.bottom;

		const isVerticallyOverlapping =
			verticallyStartsOutEndsIn ||
			verticallyStartsInEndsIn ||
			verticallyStartsInEndsOut;

		// X axis (horixontal) overlap checks

		const horizontallyStartsOutEndsIn =
			areaB.left <= areaA.left && areaB.right >= areaA.left;

		const horizontallyStartsInEndsIn =
			areaB.left >= areaA.left && areaB.right <= areaA.right;

		const horizontallyStartsInEndsOut =
			areaB.left <= areaA.right && areaB.right >= areaA.right;

		const isHorizontallyOverlapping =
			horizontallyStartsOutEndsIn ||
			horizontallyStartsInEndsIn ||
			horizontallyStartsInEndsOut;

		const areAreasOverlapping =
			isVerticallyOverlapping && isHorizontallyOverlapping;

		return areAreasOverlapping;
	}

	calculateObjectsSelection(area: IArea): (CanvasObject | DraggableObject)[] {
		if (this.disabled) {
			this.selectedObjects.forEach((canvasObject) => {
				if (canvasObject.selected) {
					this.unselect(canvasObject);
				}
			});

			this.selectedObjects = [];
			return [];
		}

		const selectedObjects = [
			...this.nonDraggableObjects,
			...this.draggableObjects
		].filter((canvasObject) => {
			const objectCoords = this.getObjectCoords(canvasObject);

			const isObjectInSelection = this.resolveAreasOverlap(
				{
					left: area.left,
					top: area.top,
					right: area.right,
					bottom: area.bottom
				},
				{
					left: objectCoords.left,
					top: objectCoords.top,
					right: objectCoords.right,
					bottom: objectCoords.bottom
				}
			);

			let isObjectSelected = false;

			// If the object is inside the current selection area
			if (isObjectInSelection) {
				// If the object isn't yet in the current selection area list, add it
				if (
					!this.selectedInCurrentSelectionAction.includes(
						canvasObject
					) &&
					!canvasObject.selected
				) {
					this.selectedInCurrentSelectionAction.push(canvasObject);
				}

				// If the object is not selected, select it
				if (!canvasObject.selected) {
					this.select(canvasObject);
				}

				isObjectSelected = true;

				// If the object is not inside the current selection area
			} else {
				// If the object is in the current selection area list, remove it
				if (
					this.selectedInCurrentSelectionAction.includes(canvasObject)
				) {
					this.selectedInCurrentSelectionAction =
						this.selectedInCurrentSelectionAction.filter(
							(object) => object !== canvasObject
						);

					// If the object is selected, unselect it
					if (canvasObject.selected) {
						this.unselect(canvasObject);
					}
				}

				// If the object is selected, but not currently in the selection area, and the user is not holding shift, unselect it
				if (canvasObject.selected && !this.isMultiselecting) {
					this.unselect(canvasObject);
				}

				// If the object is selected and not currently in the selection area, but the user is holding shift, keep it selected
				if (canvasObject.selected && this.isMultiselecting) {
					isObjectSelected = true;
				}
			}

			return isObjectSelected;
		});

		this.selectedObjects = selectedObjects as CanvasObject[];
		return selectedObjects;
	}

	getObjectOverlaps(
		canvasObject: DraggableObject | CanvasObject,
		newCoordinate?: ICoordinates
	): OverlapReport {
		const overlapps: OverlapReport = [];

		const actualCoords = this.getObjectCoords(canvasObject);

		const newCoords = this.getObjectCoords(canvasObject, newCoordinate);

		this.draggableObjects.forEach((curObject) => {
			if (curObject === canvasObject) {
				return;
			}

			// If they are connected to each other, we have to take the arrows into consideration on the overlap calculation
			const connection = (
				(canvasObject.connections || []) as Connection[]
			).find(
				(conn) =>
					conn.sourceParentObject === curObject ||
					conn.targetParentObject === curObject
			);

			const areConnected = !!connection;

			// If the objects are connected, add the scaled pixel amount that the arrows on either side need to display correctly before they break
			// The connections direction, should also be considered before adding the scaled pixel amount to the left ot right values of the object
			// To avoid the object from being dragged over and around, the of the collision area should be consireded infinite
			// We want it to always consider it an overlap with a connection if the object is further left or right than the distance needed to display the arrow regardless of vertical position

			const curObjOgCoords: IArea = this.getObjectCoords(curObject);

			let curObjCoords: IArea = { ...curObjOgCoords };

			let nextValidX: number = canvasObject.x;
			let nextValidY: number = canvasObject.y;

			if (areConnected) {
				// If it is the target object, the arrow is on the left
				const curObjIsOnTheRight =
					connection.targetParentObject === curObject;

				// Add connection padding to the left and right of the object, this only affect to this particular two objects' interaction
				curObjCoords = {
					// If the object we are checking against is on the right, we extend the left side of the area to the left of the canvas
					// Otherwise we add only some padding to the left side of
					left: curObjIsOnTheRight
						? curObjOgCoords.left - MIN_ARROW_AREA_PX_WIDTH
						: 0,

					// If the object we are checking against is on the right, we extend the right side of the area to the right of the canvas
					// Otherwise we add only some padding to the right side of the area
					right: curObjIsOnTheRight
						? this.canvasElement.offsetWidth
						: curObjOgCoords.right + MIN_ARROW_AREA_PX_WIDTH,

					// We extend the top of the area to the top of the canvas, and the bottom to the bottom of the canvas
					top: 0,
					bottom: this.canvasElement.offsetHeight
				};

				if (curObjIsOnTheRight) {
					const mainObjectWidth =
						actualCoords.right - actualCoords.left;

					nextValidX =
						curObjCoords.left -
						MIN_ARROW_AREA_PX_WIDTH -
						5 -
						mainObjectWidth;
				} else {
					nextValidX =
						curObjCoords.right + MIN_ARROW_AREA_PX_WIDTH + 5;
				}
			}

			const currentCoordsOverlap = this.resolveAreasOverlap(
				actualCoords,
				curObjCoords
			);

			const newCoordsOverlap = this.resolveAreasOverlap(
				newCoords,
				curObjCoords
			);

			let currentCoordsOverlapDef: IOverlapDefinition | null = null;
			let altCoordsOverlapDef: IOverlapDefinition | null = null;

			if (currentCoordsOverlap) {
				currentCoordsOverlapDef = {
					connection,
					overlappingObject: curObject,
					nextValidCoordinates: {
						x: nextValidX,
						y: nextValidY
					}
				};
			}

			if (newCoordsOverlap) {
				altCoordsOverlapDef = {
					connection,
					overlappingObject: curObject,
					nextValidCoordinates: {
						x: nextValidX,
						y: nextValidY
					}
				};
			}

			if (currentCoordsOverlapDef || altCoordsOverlapDef) {
				overlapps.push({
					coords: currentCoordsOverlapDef,
					altCoords: altCoordsOverlapDef
				});
			}
		});

		return overlapps;
	}

	dragObjects(
		draggableObjects: DraggableObject[],
		{ movementX, movementY }: { movementX: number; movementY: number }
	) {
		// Loop over objects and call their on drag method with the movement values
		draggableObjects.forEach((draggableObject) => {
			draggableObject.move({ movementX, movementY });

			// HERE
			this.resizeCanvasToFitObject(draggableObject);

			// Check if the new object is outside the current canvas area
			// Then increase the canvas size if needed
			// const newObjectBottom =
			// 	draggableObject.y +
			// 	draggableObject.element.offsetHeight * this.currentScale;
			// const newObjectRight =
			// 	draggableObject.x +
			// 	draggableObject.element.offsetWidth * this.currentScale;

			// if (newObjectBottom > this.contentBottom) {
			// 	this.contentBottom = newObjectBottom;
			// }

			// if (newObjectBottom > this.canvasElement.offsetHeight) {
			// 	this.canvasElement.style.height = `${newObjectBottom + 10000}px`;
			// }

			// if (newObjectRight > this.contentRight) {
			// 	this.contentRight = newObjectRight;
			// }

			// if (newObjectRight > this.canvasElement.offsetWidth) {
			// 	this.canvasElement.style.width = `${newObjectRight + 10000}px`;
			// }
		});
	}

	resizeCanvasToFitObject(canvasObject: CanvasObject | DraggableObject) {
		// Check if the new object is outside the current canvas area
		// Then increase the canvas size if needed
		const newObjectBottom =
			canvasObject.y +
			canvasObject.element.offsetHeight * this.currentScale;
		const newObjectRight =
			canvasObject.x +
			canvasObject.element.offsetWidth * this.currentScale;

		let differenceRight = 0;
		let differenceBottom = 0;

		if (newObjectBottom > this.contentBottom) {
			this.contentBottom = newObjectBottom;
		}
		if (newObjectBottom > this.canvasElement.offsetHeight) {
			differenceBottom =
				newObjectBottom - this.canvasElement.offsetHeight;
			this.canvasElement.style.height = `${newObjectBottom + 10000}px`;
		}
		if (newObjectRight > this.contentRight) {
			this.contentRight = newObjectRight;
		}
		if (newObjectRight > this.canvasElement.offsetWidth) {
			differenceRight = newObjectRight - this.canvasElement.offsetWidth;
			this.canvasElement.style.width = `${newObjectRight + 10000}px`;
		}
	}

	resizeCanvasToFitObjects(
		canvasObjects: (CanvasObject | DraggableObject)[]
	) {
		canvasObjects.forEach((canvasObject) =>
			this.resizeCanvasToFitObject(canvasObject)
		);
	}

	connectionToUndoableValue(
		connection: Connection,
		options: {
			noInstance?: boolean;
			shouldMarkAsDeleted?: boolean;
		} = {
			shouldMarkAsDeleted: false,
			noInstance: false
		}
	): IConnectionValue {
		return {
			id: Connection.getId(connection.startId, connection.endId),
			startId: connection.startId,
			endId: connection.endId,
			instance: options.noInstance ? null : connection,
			deleted: !!options.shouldMarkAsDeleted,
			initialOptions: {
				...connection.initialOptions,
				complementaryColor: connection.complementaryColor
			},
			sourceParentObject: {
				objectId: connection.sourceParentObject.id,
				objectType:
					connection.sourceParentObject instanceof DraggableObject
						? 'draggable'
						: 'non-draggable'
			},
			targetParentObject: {
				objectId: connection.targetParentObject.id,
				objectType:
					connection.targetParentObject instanceof DraggableObject
						? 'draggable'
						: 'non-draggable'
			},
			selected: connection.selected,
			type: 'connection'
		};
	}

	connectionsToUndoableValues(
		connections: Connection[],
		options: {
			noInstance?: boolean;
			shouldMarkAsDeleted?: boolean;
			deletedConnections?: string[];
		} = {
			shouldMarkAsDeleted: false,
			noInstance: false,
			deletedConnections: []
		}
	): IConnectionValue[] {
		return connections.map((connection) =>
			this.connectionToUndoableValue(connection, {
				noInstance: options.noInstance,
				shouldMarkAsDeleted:
					options.shouldMarkAsDeleted ||
					options.deletedConnections?.includes(connection.id)
			})
		);
	}

	toUndoableValue(
		object: DraggableObject | CanvasObject,
		options: {
			noInstance?: boolean;
			shouldMarkAsDeleted?: boolean;
			deletedConnections?: string[];
		} = {
			shouldMarkAsDeleted: false,
			noInstance: false,
			deletedConnections: []
		}
	): IDraggableUndoableValue {
		return {
			id: object.id,
			x: object.x,
			y: object.y,
			selected: object.selected,
			// element: object.element,
			instance: options.noInstance ? null : object,
			connections: this.connectionsToUndoableValues(
				object.connections,
				options
			),
			deleted: !!options.shouldMarkAsDeleted,
			selectable: object.selectable,
			deletable: object.deletable,
			disabled: object.disabled,
			type:
				object instanceof DraggableObject
					? 'draggable'
					: 'non-draggable'
		};
	}

	toUndoableValues(
		objects: (DraggableObject | CanvasObject)[],
		options: {
			noInstance?: boolean;
			shouldMarkAsDeleted?: boolean;
			deletedConnections?: string[];
		} = {
			shouldMarkAsDeleted: false,
			noInstance: false,
			deletedConnections: []
		}
	): IDraggableUndoableValue[] {
		return objects.map((obj) => this.toUndoableValue(obj, options));
	}

	// When entities will be subscribed for a change, before that change happens, we need to capture their current state
	// We do this by adding the old state of the entities that have changes, to the previous index in the change stack
	// This is because the undoable stack is a stack of changes, not a stack of states
	// Meaning, if we don't store the previous state of the entities that have changed,
	// we won't be able to undo the change since we would only know what it change to, not what it changed from
	captureValuesBeforeUndoableChange(
		oldValuesForEntitiesAboutToChange: IDraggableUndoableValue[]
	) {
		const prevStackIndexModified: DraggableObject[] = [
			...this.undoValueStack[this.undoableStackValueIndex],
			...oldValuesForEntitiesAboutToChange
		];

		// Merge values that refer to the same object
		const mergedObjects = prevStackIndexModified.reduce((acc, current) => {
			const x = acc.find((item) => item.id === current.id);

			if (!x) {
				return acc.concat([current]);
			} else {
				return acc;
			}
		}, []);

		this.undoValueStack[this.undoableStackValueIndex] = mergedObjects;
	}

	// This function should be triggered whenever we want to persist a change in the selected objects array
	captureUndoableChange(newValues: IDraggableUndoableValue[]) {
		if (!this.enableUndoRedo) {
			return;
		}
		this.persistScroll();

		this.undoableChange(newValues, this.id);

		this.restoreScroll();
	}

	dragStart() {
		// Set body to user select none to avoid text selection while dragging
		document.body.style.userSelect = 'none';
		// Webkit user select none for Safari
		document.body.style.webkitUserSelect = 'none';

		// Capture the initial values of the selected objects
		const valuesSoonToChange = this.toUndoableValues(
			this.selectedObjects as DraggableObject[]
		);

		// Capture the initial values of the selected objects before they change
		this.captureValuesBeforeUndoableChange(valuesSoonToChange);
	}

	// Loops over all selected objects and calls their drag end method,
	// Then subscribes this change on the undo stack
	dragEnd() {
		// Re-enable text selection after dragging
		document.body.style.userSelect = 'auto';
		// Webkit user select auto for Safari
		document.body.style.webkitUserSelect = 'auto';

		if (this.selectedObjects.length === 0) {
			return;
		}

		const selectedObjects = [...this.selectedObjects].filter(
			(obj) =>
				!obj.deleted &&
				!obj.disabled &&
				obj instanceof DraggableObject &&
				!this.disabled
		);

		if (selectedObjects.length === 0) {
			return;
		}

		this.selectedObjects.forEach((draggableObject) => {
			if (draggableObject instanceof DraggableObject) {
				draggableObject.dragEnd();
			}
		});

		const values = this.toUndoableValues(
			this.selectedObjects as DraggableObject[]
		);

		this.captureUndoableChange(values);

		this.onDragEndCallback?.(selectedObjects);
	}

	draggable({
		element,
		coordinates,
		id
	}: {
		element: HTMLElement;
		coordinates: ICoordinates;
		id: string;
	}): DraggableObject {
		const draggableObject = new DraggableObject({
			element,
			coordinates,
			canvas: this,
			id
		});

		this.draggableObjects.push(draggableObject);

		this.initialValues.push(this.toUndoableValues([draggableObject])[0]);

		// Here

		// Check if the new object is outside the current canvas area
		// Then increase the canvas size if needed
		this.resizeCanvasToFitObject(draggableObject);
		// const newObjectBottom =
		// 	draggableObject.y +
		// 	draggableObject.element.offsetHeight * this.currentScale;
		// const newObjectRight =
		// 	draggableObject.x +
		// 	draggableObject.element.offsetWidth * this.currentScale;

		// if (newObjectBottom > this.contentBottom) {
		// 	this.contentBottom = newObjectBottom;
		// }

		// if (newObjectBottom > this.canvasElement.offsetHeight) {
		// 	this.canvasElement.style.height = `${newObjectBottom + 10000}px`;
		// }

		// if (newObjectRight > this.contentRight) {
		// 	this.contentRight = newObjectRight;
		// }

		// if (newObjectRight > this.canvasElement.offsetWidth) {
		// 	this.canvasElement.style.width = `${newObjectRight + 10000}px`;
		// }

		return draggableObject;
	}

	object(
		options: Pick<
			ICanvasObjectCreateOptions,
			'coordinates' | 'deletable' | 'element' | 'id' | 'selectable'
		>
	): CanvasObject {
		const canvasObject = new CanvasObject({
			...options,
			canvas: this
		});

		this.nonDraggableObjects.push(canvasObject);

		return canvasObject;
	}

	removeAllRelativeObjects() {
		this.draggableObjects.forEach((draggableObject) => {
			draggableObject.removeAllRelativeObjects();
		});
	}

	hideAllRelativeObjects() {
		this.draggableObjects.forEach((draggableObject) => {
			draggableObject.hideAllRelativeObjects();
		});
	}

	removeAllRelativeObjectsExcept(relativeObject: RelativeObject) {
		let hasFound = false;

		this.draggableObjects.forEach((draggableObject) => {
			// If the relative object is found, we can normally delete all other relative objects without checking
			if (hasFound) {
				draggableObject.removeAllRelativeObjects();
				return;
			}

			const { hasFound: hasFoundInChildren } =
				draggableObject.removeAllRelativeObjectsExcept(relativeObject);

			if (hasFoundInChildren) {
				hasFound = true;
			}
		});
	}

	hideAllRelativeObjectsExcept(relativeObject: RelativeObject) {
		let hasFound = false;

		this.draggableObjects.forEach((draggableObject) => {
			// If the relative object is found, we can normally delete all other relative objects without checking
			if (hasFound) {
				draggableObject.hideAllRelativeObjects();
				return;
			}

			const { hasFound: hasFoundInChildren } =
				draggableObject.hideAllRelativeObjectsExcept(relativeObject);

			if (hasFoundInChildren) {
				hasFound = true;
			}
		});
	}

	disconnect(
		startId: string,
		endId: string,
		obj: DraggableObject | CanvasObject
	) {
		const connectionId = Connection.getId(startId, endId);

		const connection = obj.connections.find(
			(conn) => conn.id === connectionId
		);

		if (connection) {
			connection.remove();
		}
	}

	connect(
		source: HTMLElement,
		startId: string,
		target: HTMLElement,
		endId: string,
		sourceParentObject: DraggableObject | CanvasObject,
		targetParentObject: DraggableObject | CanvasObject,
		options: IConnectionOptions = {},
		debug: boolean = false
	): Connection {
		const newConnectionValue: IConnectionValue = {
			id: Connection.getId(startId, endId),
			startId,
			endId,
			instance: null,
			deleted: false,
			sourceParentObject: {
				objectId: sourceParentObject.id,
				objectType:
					sourceParentObject instanceof DraggableObject
						? 'draggable'
						: 'non-draggable'
			},
			targetParentObject: {
				objectId: targetParentObject.id,
				objectType:
					targetParentObject instanceof DraggableObject
						? 'draggable'
						: 'non-draggable'
			},
			initialOptions: options,
			selected: false,
			type: 'connection'
		};

		if (debug) {
			debugger;
		}

		// Connect only if the connection doesn't already exist
		const existingConnection = sourceParentObject.connections.find(
			(connection) => {
				return DraggableObject.isEquivalentConnection(
					this.connectionToUndoableValue(connection),
					newConnectionValue
				);
			}
		);

		if (existingConnection) {
			if (debug) {
				debugger;
			}
			existingConnection.deleted = false;
			existingConnection.refresh(debug);
			return existingConnection;
		}

		if (debug) {
			debugger;
		}

		const connection = new Connection(
			source,
			startId,
			target,
			endId,
			options,
			this,
			sourceParentObject,
			targetParentObject,
			debug
		);

		// Subscribe the connection to both objects
		sourceParentObject.subscribeConnection(connection);
		targetParentObject.subscribeConnection(connection);

		return connection;
	}

	// Starts a dragging connection, this is when the user is drawing a connection line between two objects
	// One object is known, since the user started dragging from it, the other object is unknown
	startConnection({
		sourceId,
		targetId,
		parentObject
	}: {
		sourceId?: string;
		targetId?: string;
		parentObject: DraggableObject | CanvasObject;

		// Returns all the handlers to hook into the events that are triggered when the user is drawing a connection
	}): ConnectionDrawing {
		// Remove previous connection drawing if there is one
		this.removeConnectionDrawing();

		const newConnection = new ConnectionDrawing(
			{
				sourceId,
				targetId,
				parentObject
			},
			this
		);

		return newConnection;
	}

	removeConnectionDrawing() {
		this.activeConnectionDrawing?.remove();
		this.activeConnectionDrawing = null;
	}

	onBeforeScrollToThis(callback: (objectId: string) => Promise<void> | void) {
		this.onBeforeScrollToThisCallback = callback;
	}

	onDragEnd(callback: (objects: (CanvasObject | DraggableObject)[]) => void) {
		this.onDragEndCallback = callback;
	}

	onDelete(callback: (objects: (CanvasObject | DraggableObject)[]) => void) {
		this.onDeleteCallback = callback;
	}

	scrollToObjectById(id: string, onScrollEnd?: () => void) {
		// @ts-ignore
		if (window.onscrollend !== undefined) {
			window.document.addEventListener(
				'scrollend',
				() => {
					this.persistScroll();
					onScrollEnd?.();
				},
				{
					once: true
				}
			);
		} else {
			Logger.warn("Your browser doesn't support the scrollend event");
			setTimeout(() => {
				this.persistScroll();
				onScrollEnd?.();
			}, 300);
		}

		// On before scroll callback
		if (!!this.onBeforeScrollToThisCallback) {
			const result = this.onBeforeScrollToThisCallback(id);

			// If it is a promise, use then
			if (result instanceof Promise) {
				result.then(() => {
					const element = Canvas.selectByAttribute(id);

					if (!element) {
						Logger.warn(
							'Element not found, at scroll to canvas object with id: ' +
								id
						);
						return;
					}

					element.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
						inline: 'center'
					});

					// Find object being scrolled to then call the 'onScrollToThisCallback' on the object if it exists
					const canvasObject = this.draggableObjects.find(
						(object) => object.id === id
					);

					if (canvasObject && canvasObject.onScrollToThisCallback) {
						canvasObject.onScrollToThisCallback();
					}
				});
			} else {
				const element = Canvas.selectByAttribute(id);

				if (!element) {
					Logger.warn(
						'Element not found, at scroll to canvas object with id: ' +
							id
					);
					return;
				}

				element.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'center'
				});

				// Find object being scrolled to then call the 'onScrollToThisCallback' on the object if it exists
				const canvasObject = this.draggableObjects.find(
					(object) => object.id === id
				);

				if (canvasObject && canvasObject.onScrollToThisCallback) {
					canvasObject.onScrollToThisCallback();
				}
			}
		} else {
			const element = Canvas.selectByAttribute(id);

			if (!element) {
				Logger.warn(
					'Element not found, at scroll to canvas object with id: ' +
						id
				);
				return;
			}

			element.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'center'
			});

			// Find object being scrolled to then call the 'onScrollToThisCallback' on the object if it exists
			const canvasObject = this.draggableObjects.find(
				(object) => object.id === id
			);

			if (canvasObject && canvasObject.onScrollToThisCallback) {
				canvasObject.onScrollToThisCallback();
			}
		}
	}

	scrollTo(x: number, y: number) {
		window.scrollTo(x, y);
		this.persistScroll();
	}

	scrollToObjectByIdInstantly(id: string) {
		const element = Canvas.selectByAttribute(id);

		if (!element) {
			return;
		}

		element.scrollIntoView({
			behavior: 'auto',
			block: 'center',
			inline: 'center'
		});

		this.persistScroll();
	}

	private applyScale(newScale: number) {
		// Enforce a minimum
		let sanitized = newScale;
		if (sanitized < this.minScale) sanitized = this.minScale;
		if (sanitized > this.maxScale) sanitized = this.maxScale;

		this.currentScale = sanitized;
		this.canvasElement.style.transform = `scale(${sanitized.toString()})`;

		this.onScaleChangeCallback?.(sanitized);

		// Persist in localStorage
		localStorage.setItem(
			`canvas-${this.id}-scale`,
			this.currentScale.toString()
		);
	}

	persistScrollToLocalStorage() {
		localStorage.setItem(
			`canvas-${this.id}-scroll-x`,
			this.naiveUnscaledDocumentScrollX.toString()
		);

		localStorage.setItem(
			`canvas-${this.id}-scroll-y`,
			this.naiveUnscaledDocumentScrollY.toString()
		);
	}

	retrieveScrollFromLocalStorage(): ICoordinates {
		const scrollX = localStorage.getItem(`canvas-${this.id}-scroll-x`);
		const scrollY = localStorage.getItem(`canvas-${this.id}-scroll-y`);

		return {
			x: parseFloat(scrollX || '0'),
			y: parseFloat(scrollY || '0')
		};
	}
}
